/**
 * FortiGate traffic dashboard: the Angular component of the device page
 * (it also embeds the all-devices page, see fortinet-fleet.component.ts).
 *
 *   Component class:   FortinetDashboardComponent
 *   Service class:     FortinetDashboardService
 *   Selector:          app-fortinet-dashboard
 *   Shared helpers:    fortinet-shared.ts
 *
 * The page is split in tabs (overview, traffic, interfaces, policies,
 * VPN and WAN, system). The device, the history window and the tab are kept
 * in the URL query (fgDevice, fgRange, fgTab), so I can bookmark or share a
 * view.
 */
import { CommonModule, Location } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injectable,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
  ViewChild,
  inject,
} from '@angular/core';
import { ClrProgressBarModule, ClrSpinnerModule } from '@clr/angular';
import { EMPTY, Observable, Subject, merge } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import type { EChartsOption, SeriesOption } from 'echarts';

import { FleetDocument, FleetFilters, FortinetFleetComponent } from './fortinet-fleet.component';
import { FortinetInterfacesComponent, PortsDoc } from './fortinet-interfaces.component';
import { FortinetNetmapComponent, NetmapDoc, NetmapState } from './fortinet-netmap.component';
import { environment } from '../../environments/environment';
import {
  AckFields, AckNoteEditor, Anonymizer, Badge, Band, ChartKit, DeviceRef,
  DeviceTab, EVENT_KIND_LABEL, EventKind, EventSev, Evidence, FONT,
  FORTINET_FLEET_VIEW, FORTINET_USER, FortiEvent,
  FortinetNavService, NavCommand, NavDevice, NavEntry, NavGroup, NavSev,
  compareGroupsAsTree, groupLeafName, groupPathText, groupsOfDevice, groupsWithParents, mainGroupOf,
  ackNoteLine, eventBadge,
  eventKindsOf, eventWhen, firstSeenText, firstSeenTitle, Formatter, HistoryRange, Level, Secret,
  SEV_RANK, Sev, Thresholds, Values, ackNote, daysLevelOf, describeApiError, downloadCsv,
  esc, evidenceOf,
  fmtAge, fmtBps, fmtBytes, fmtNum, fmtPct, fmtPctOrNum, fmtRate, fmtStamp,
  isPct, issueBadge, issueMatches, levelCls, levelOf, pollTriggers,
  SortValue, TableSort, issuesTabFromKey, sortAckedIssues, sortOpenIssues, thresholdsOf,
} from './fortinet-shared';

// Kept exported from here for code that imported them from this file.
export { fmtBps, fmtBytes, fmtNum, fmtPct, fmtRate } from './fortinet-shared';
export type { Band, HistoryRange, Values } from './fortinet-shared';


/* ============================================================================
 * Document shape (GET /data.json)
 * ========================================================================== */

export interface DashboardMeta {
  serial: string;
  /** every device selectable via ?serial=<s> (absent on older backends) */
  serials?: string[];
  version: string | null;
  source: string;
  records: Record<string, number>;
  history_range: string | null;
  history_ranges: HistoryRange[];
  /** set by a live collector when the last device poll failed */
  last_error?: string | null;
  /** true when at least one time series was grouped into min/avg/max
   *  buckets (wide windows); absent on older backends */
  bucketed?: boolean;
  /** verdict thresholds (dashboard.THRESHOLDS); absent on older backends */
  thresholds?: Partial<Thresholds>;
  /** this device: label, group, freshness (absent on older backends) */
  device?: DeviceInfo;
  /** every selectable device with its label (absent on older backends) */
  devices?: DeviceRef[];
}

/** meta.device: dashboard.device_info */
export interface DeviceInfo {
  serial: string;
  host: string | null;
  label: string;
  /** own hostname, when the HA member list reports it */
  hostname: string | null;
  /** main group first (config.yaml `groups:`); absent from older backends,
   *  read it with groupsOfDevice() */
  groups?: string[];
  /** the single group sent by older backends */
  group?: string | null;
  firmware_target: string | null;
  firmware_status: 'on-target' | 'behind' | 'ahead' | null;
  last_poll: number | null;   // epoch ms
  /** seconds, when the document was served */
  poll_age: number | null;
  interval: number | null;    // seconds
  stale: boolean;
}

export interface SessionsTs {
  t: number[]; // epoch ms
  bucketed?: boolean;
  session_count: Values;
  setup_rate: Values;
  npu: Values;
  nturbo: Values;
  hw: Values;
  session_count_min?: Values;
  session_count_max?: Values;
  setup_rate_min?: Values;
  setup_rate_max?: Values;
}

export interface NameValue {
  name: string;
  value: number;
}

export interface Talker {
  name: string;
  sent: number;
  rcvd: number;
}

/** The old flat list of the network map (still sent, no longer drawn). */
export interface MapHost {
  intf: string;
  host: string;
  bytes: number;
}

export interface TrafficBucket {
  id: string;
  label: string;
  t_start: number | null;
  t_end: number | null;
  polls: number;
  distinct_sessions: number;
  protocols: NameValue[];
  /** top hosts by bytes actually transmitted (newer backends) */
  senders?: NameValue[];
  /** true when the session budget ran out before the oldest poll of the
   *  window (dashboard.build_session_traffic): t_start is then later than
   *  the start of the window (absent from an older dashboard.py) */
  budget_cut?: boolean;
  /** legacy field from older backends: per-originator sent/received */
  talkers?: Talker[];
  dports: NameValue[];
  /** same ports ranked by total bytes (newer backends) */
  dports_bytes?: NameValue[];
  apps: NameValue[];
  scope: NameValue[];
  /** sankey: source scope → service → destination scope (newer backends) */
  scope_flows?: ScopeFlows;
  services: NameValue[];
  /** top destination addresses by total bytes (newer backends) */
  receivers?: NameValue[];
  /** legacy field from older backends: top source addresses by total bytes */
  emitters?: NameValue[];
  map_hosts?: MapHost[];
  /** the network map: interface, subnet, hosts (newer backends) */
  netmap?: NetmapDoc;
}

export interface SankeyLink { source: string; target: string; value: number; }
export interface ScopeFlows { nodes: { name: string }[]; links: SankeyLink[]; }

export interface SessionTraffic {
  latest_count: number;
  bucket: TrafficBucket | null;
}

export interface NamedSeries extends Band {
  name: string;
  /** bucket averages when the series is bucketed, raw samples otherwise */
  values: Values;
}

export interface TimeSeries {
  t: number[];
  series: NamedSeries[];
  bucketed?: boolean;
}

export interface IfaceThroughputSeries {
  name: string;
  rx: Values;
  tx: Values;
  rx_min?: Values;
  rx_max?: Values;
  tx_min?: Values;
  tx_max?: Values;
}

export interface IfaceThroughput {
  t: number[];
  series: IfaceThroughputSeries[];
  bucketed?: boolean;
}

export interface IfaceLatest {
  name: string;
  alias: string;
  ip: string;
  link: boolean;
  speed: number | null;
  rx_bytes: number;
  tx_bytes: number;
  rx_errors: number;
  tx_errors: number;
  duplex: number | null;
  rx_bps: number | null;
  tx_bps: number | null;
  /** busier direction as % of link speed, or null when speed is unknown */
  util?: number | null;
}

export interface IfaceSummary {
  total: number;
  up: number;
  down: number;
  total_rx_bps: number | null;
  total_tx_bps: number | null;
  prev_rx_bps: number | null;
  prev_tx_bps: number | null;
}

export interface PolicyTotal {
  name: string;
  bytes: number;
  hits: number;
  active_sessions: number;
  bps_now: number | null;
}

export interface DailyTraffic {
  days: string[];
  rx: number[];
  tx: number[];
}

export type IssueSeverity = 'danger' | 'warning' | 'info';

export interface Issue extends AckFields {
  sev: IssueSeverity;
  title: string;
  detail: string;
}

/* ---- device health / VPN / WAN (newer backends; all optional) ---------- */

export interface FirmwareInfo {
  current: string | null;
  /** direct next hops from the running version, sorted ascending */
  next: string[];
  /** highest version reachable through the upgrade-path table */
  latest: string | null;
}

export interface LicenseEntry {
  name: string;
  status: string;
  expires: number | null;   // epoch seconds
  days_left: number | null;
  /** "active" | "expired" | "inactive": for colour + filtering */
  tier?: 'active' | 'expired' | 'inactive';
}

export interface SensorEntry {
  name: string;
  value: number | string | null;
  alarm: boolean;
}

export interface VpnTunnel {
  name: string;
  rgwy: string;
  up: boolean;
  bps_now: number | null;
  bytes: number;
}

export interface SslUsersTs {
  t: number[];
  bucketed?: boolean;
  users: Values;
  tunnels: Values;
  users_min?: Values;
  users_max?: Values;
  tunnels_min?: Values;
  tunnels_max?: Values;
}

export interface SdwanSeries {
  name: string;
  latency: Values;
  jitter: Values;
  loss: Values;
  latency_min?: Values;
  latency_max?: Values;
  jitter_min?: Values;
  jitter_max?: Values;
  loss_min?: Values;
  loss_max?: Values;
}

export interface SdwanMember {
  name: string;
  status: string;
  latency: number | null;
  jitter: number | null;
  loss: number | null;
}

export interface Sdwan {
  t: number[];
  bucketed?: boolean;
  series: SdwanSeries[];
  latest: SdwanMember[];
}

export interface HaMember {
  hostname: string;
  serial: string;
  cpu: number | null;
  mem: number | null;
  sessions: number | null;
  is_master: boolean;
}

export interface FortiguardInfo {
  servers: number;
  connected: number;
  available: boolean;
}

export interface AdminEntry {
  name: string;
  ip: string;
  method: string;
  login_time: number | null; // epoch seconds
}

/* ---- time, certificates, routing, events (newer backends) -------------- */

/** dashboard.build_system_state (web-ui/state) */
export interface SystemState {
  available: boolean;
  conserve: boolean | null;
  /** conserve mode at some point of the window */
  conserve_seen: boolean;
  last_reboot: number | null;   // epoch ms
  /** seconds the device clock is ahead (+) of the collector's */
  clock_skew: number | null;
  clock_at: number | null;      // epoch ms
}

export interface NtpServer {
  server: string;
  ip: string;
  reachable: boolean;
  selected: boolean;
  stratum: number | null;
  offset_ms: number | null;
  delay_ms: number | null;
}

export interface NtpInfo {
  available: boolean;
  servers: NtpServer[];
  reachable: number;
}

export interface CertEntry {
  name: string;
  type: string;
  source: string;
  /** factory certificate, renewed by firmware upgrades */
  built_in: boolean;
  status: string;
  expires: number | null;   // epoch seconds
  days_left: number | null;
  subject: string;
}

export interface BgpPeer {
  neighbor: string;
  local_ip: string;
  remote_as: number | null;
  state: string;
  admin_up: boolean;
  up: boolean;
}

export interface OspfPeer {
  neighbor: string;
  router_id: string;
  priority: number | null;
  state: string;
  up: boolean;
}

export interface RoutingInfo {
  bgp_available: boolean;
  ospf_available: boolean;
  bgp: BgpPeer[];
  ospf: OspfPeer[];
}

/** Verdict of one endpoint in the "Endpoint polls" card (see
 *  _poll_verdict in dashboard.py):
 *    ok          every kept poll answered 2xx
 *    expected    an optional endpoint that only gave its "not on this
 *                device" answers: not a problem
 *    recovered   the newest poll worked, some earlier ones failed
 *    failing     the newest poll failed
 *    skipped     not polled on purpose (firmware older than min_version)
 *    not_polled  in endpoints.py but never in the poll index
 *    retired     in the poll index but no longer in endpoints.py */
export type PollVerdict = 'ok' | 'expected' | 'recovered' | 'failing'
  | 'skipped' | 'not_polled' | 'retired';

/** What an endpoint answered in one cycle: an HTTP status, null when the
 *  request got no answer, 'skip' when it was not polled in that cycle. */
export type PollMark = number | null | 'skip';

export interface PollEndpointRow {
  /** func_name in endpoints.py (the suffix for a retired endpoint) */
  name: string;
  /** file suffix, the key of the poll index */
  suffix: string;
  /** full path called on this device, query included ('' when retired) */
  path: string;
  /** 'series', 'snapshot' or 'export' ('' when retired) */
  kind: string;
  optional: boolean;
  /** the statuses meaning "not on this device" (empty when not optional) */
  expected: number[];
  /** cycles where it was polled, and how many answered 2xx or not */
  polled: number;
  ok: number;
  failed: number;
  /** status of the newest poll (null: no answer, or never polled) */
  last_status: number | null;
  /** "404 Not Found", "no answer", "not polled" */
  last_status_text: string;
  /** epoch ms of the newest 2xx and of the newest failure */
  last_ok: number | null;
  last_failure: number | null;
  /** one mark per kept cycle, oldest first (lined up with PollReport.times) */
  history: PollMark[];
  verdict: PollVerdict;
  /** why it happens and whether it's a problem ('' when all is fine) */
  explanation: string;
}

export interface PollReport {
  /** false when there is no poll index yet */
  available: boolean;
  /** cycles read, and how many the collector keeps (POLL_INDEX_KEEP) */
  cycles: number;
  kept: number;
  first_poll: number | null;
  last_poll: number | null;
  /** epoch ms of each cycle, oldest first */
  times: (number | null)[];
  /** "404" -> "Not Found", for every status that appears */
  status_names: Record<string, string>;
  /** sentences about the device that explain several rows at once */
  device_notes: string[];
  counts: Record<PollVerdict, number>;
  endpoints: PollEndpointRow[];
}

export interface DashboardDocument {
  meta: DashboardMeta;
  sessions_ts: SessionsTs;
  session_traffic: SessionTraffic;
  policy_throughput: TimeSeries;
  policy_sessions: TimeSeries;
  policy_totals: PolicyTotal[];
  iface_throughput: IfaceThroughput;
  iface_latest: IfaceLatest[];
  /** what is open and pinned on this device's network map (saved on the
   *  server, null when never saved; missing on older backends) */
  netmap_state?: NetmapState | null;
  iface_summary: IfaceSummary;
  daily_traffic: DailyTraffic;
  /** open issues only */
  issues: Issue[];
  /** open issues before the list was capped */
  issues_total?: number;
  /** acknowledged issues; absent from an older dashboard.py, which cannot
   *  acknowledge (the checkboxes are hidden then) */
  issues_acked?: Issue[];
  memory_ts: TimeSeries;
  /** absent in documents built by an older dashboard.py */
  cpu_ts?: TimeSeries;
  /** all below absent in documents built by an older dashboard.py */
  iface_errors?: TimeSeries;
  firmware?: FirmwareInfo;
  license?: LicenseEntry[];
  sensors?: SensorEntry[];
  vpn_tunnels?: VpnTunnel[];
  ssl_users_ts?: SslUsersTs;
  sdwan?: Sdwan;
  ha?: { members: HaMember[] };
  fortiguard?: FortiguardInfo;
  admins?: AdminEntry[];
  /** cleanup candidates (absent on older backends) */
  policy_hygiene?: { unused: string[]; oversize: string[] };
  /** all below absent on older backends */
  system_state?: SystemState;
  ntp?: NtpInfo;
  certificates?: CertEntry[];
  routing?: RoutingInfo;
  /** newest first, capped; events_total is the count before the cap */
  events?: FortiEvent[];
  events_total?: number;
  /** Interfaces tab: front panel and configured interfaces (absent on
   *  older backends) */
  ports?: PortsDoc;
  /** System tab, "Endpoint polls" card: what every endpoint answered on
   *  the last polls (absent on older backends) */
  poll_report?: PollReport;
}

/* ============================================================================
 * Pure pipes: Angular keeps their output between change-detection cycles.
 * ========================================================================== */

@Pipe({ name: 'fmtBps', pure: true, standalone: true })
export class FmtBpsPipe implements PipeTransform {
  transform(value: number | null | undefined): string {
    return fmtBps(value);
  }
}

@Pipe({ name: 'fmtBytes', pure: true, standalone: true })
export class FmtBytesPipe implements PipeTransform {
  transform(value: number | null | undefined): string {
    return fmtBytes(value);
  }
}

@Pipe({ name: 'fmtNum', pure: true, standalone: true })
export class FmtNumPipe implements PipeTransform {
  transform(value: number | null | undefined): string {
    return fmtNum(value);
  }
}

@Pipe({ name: 'fmtPct', pure: true, standalone: true })
export class FmtPctPipe implements PipeTransform {
  transform(value: number | null | undefined): string {
    return fmtPctOrNum(value);
  }
}

/** { cls, text } of an issue badge. */
@Pipe({ name: 'issueBadge', pure: true, standalone: true })
export class IssueBadgePipe implements PipeTransform {
  transform(issue: Issue): Badge {
    return issueBadge(issue.sev);
  }
}

/* ============================================================================
 * View-model types for the template
 * ========================================================================== */

export interface Trend {
  /** up-bad: a rise that is bad news (CPU, memory) */
  cls: 'up' | 'down' | 'up-bad';
  arrow: '▲' | '▼';
  pct: string; // absolute %, no sign
  title: string;
}

export interface KpiVm {
  label: string;
  value: string;
  /** rendered dimmed after the value, e.g. "/ 12 up" or " /s" */
  suffix?: string;
  trend?: Trend | null;
  sub?: string;
  badge?: Badge | null;
  /** tab opened when the card is selected */
  tab?: DeviceTab;
}

export type CheckStatus = 'pass' | 'note' | 'ack' | 'warn' | 'fail';

export interface CheckVm {
  id: string;
  label: string;
  metric: string;
  status: CheckStatus;
  badge: Badge;
  /** the issues behind the verdict, one per line */
  title: string;
  evidence: Evidence | null;
}

export interface CheckGroupVm {
  name: string;
  checks: CheckVm[];
}

/** One row of the routing neighbors table: the BGP and OSPF peers are
 *  mixed in one list, so the table can sort them together. */
export interface RoutingRowVm {
  protocol: 'BGP' | 'OSPF';
  neighbor: string;
  /** text of the state pill */
  state: string;
  /** class of the state pill */
  cls: string;
  details: string;
  /** data-ev attribute of the row ("bgp:10.0.0.1"), for the evidence links */
  ev: string;
}

/** One little square of the history strip of the Endpoint polls card. */
export interface PollMarkVm {
  /** pm-ok, pm-expected, pm-fail, pm-none (no answer) or pm-skip */
  cls: string;
  /** "2026-10-02 10:40: 404 Not Found" */
  title: string;
}

/** One row of the Endpoint polls card, with everything the template
 *  needs already computed (built once per document, in buildPollRows). */
export interface PollRowVm {
  row: PollEndpointRow;
  badge: Badge;
  marks: PollMarkVm[];
  /** "28 of 30 OK" */
  okText: string;
}

/** The buttons above the Endpoint polls table. An empty verdict list
 *  means "every row". */
export type PollFilter = 'all' | 'look' | 'fine' | 'ok';

const POLL_FILTERS: { id: PollFilter; label: string; verdicts: PollVerdict[] }[] = [
  { id: 'all', label: 'All', verdicts: [] },
  { id: 'look', label: 'To look at', verdicts: ['failing', 'recovered', 'not_polled', 'retired'] },
  { id: 'fine', label: 'Not a problem', verdicts: ['expected', 'skipped'] },
  { id: 'ok', label: 'OK', verdicts: ['ok'] },
];

/** The pill of each verdict. */
const POLL_BADGE: Record<PollVerdict, Badge> = {
  ok: { cls: 'label-success', text: 'OK' },
  expected: { cls: 'label-info', text: 'not a problem' },
  skipped: { cls: 'label-info', text: 'skipped' },
  recovered: { cls: 'label-warning', text: 'recovered' },
  not_polled: { cls: 'label-warning', text: 'not polled' },
  failing: { cls: 'label-danger', text: 'failing' },
  retired: { cls: 'label-info', text: 'retired' },
};

/** Order of the State column: the problems first. */
const POLL_RANK: Record<PollVerdict, number> = {
  failing: 0, recovered: 1, not_polled: 2, retired: 3, expected: 4, skipped: 5, ok: 6,
};

/** Errors column of the interface table: coloured from the issues raised
 *  on the window, not from the lifetime counter it shows. */
interface IfaceErrVm {
  cls: string;
  title: string;
}

interface PickerGroup {
  label: string;
  devices: DeviceRef[];
}

/* ============================================================================
 * Table sorting: the value of a row for each column (see TableSort in
 * fortinet-shared.ts). The key is the one written in the template, in
 * sortTable(..., 'key'). null means "nothing to sort on": the row goes to
 * the bottom.
 * ========================================================================== */

/** Order of a pill: the problems first (red, orange), then the rest. */
const PILL_RANK: Record<string, number> = {
  'label-danger': 0, 'label-warning': 1, 'label-info': 2, 'label-success': 3,
};

function ifaceSortValue(row: IfaceLatest, key: string): SortValue {
  switch (key) {
    case 'name': return row.name;
    // false (down) is 0, so the down links come first when ascending
    case 'link': return row.link;
    // 0 is half duplex, so those come first when ascending
    case 'duplex': return row.duplex;
    case 'rx': return row.rx_bps;
    case 'tx': return row.tx_bps;
    case 'util': return row.util;
    case 'errors': return row.rx_errors + row.tx_errors;
    default: return null;
  }
}

function policySortValue(policy: PolicyTotal, key: string): SortValue {
  switch (key) {
    case 'name': return policy.name;
    case 'now': return policy.bps_now;
    case 'sessions': return policy.active_sessions;
    case 'hits': return policy.hits;
    case 'total': return policy.bytes;
    default: return null;
  }
}

function licenseSortValue(license: LicenseEntry, key: string): SortValue {
  if (key === 'name') {
    return license.name;
  }
  // status column: the days left, so the expired and the soonest first
  if (license.days_left != null) {
    return license.days_left;
  }
  // expired but without a date: before everything that still has days
  const status = (license.status || '').toLowerCase();
  if (license.tier === 'expired' || status.includes('expired')) {
    return -1;
  }
  return null;
}

function sdwanSortValue(member: SdwanMember, key: string): SortValue {
  switch (key) {
    case 'name': return member.name;
    // the members that are not up come first
    case 'state': return (member.status || '').toLowerCase() === 'up';
    case 'latency': return member.latency;
    case 'jitter': return member.jitter;
    case 'loss': return member.loss;
    default: return null;
  }
}

function tunnelSortValue(tunnel: VpnTunnel, key: string): SortValue {
  switch (key) {
    case 'name': return tunnel.name;
    // down tunnels first
    case 'state': return tunnel.up;
    case 'now': return tunnel.bps_now;
    case 'total': return tunnel.bytes;
    default: return null;
  }
}

function routingSortValue(row: RoutingRowVm, key: string): SortValue {
  switch (key) {
    case 'protocol': return row.protocol;
    case 'neighbor': return row.neighbor;
    case 'state': return PILL_RANK[row.cls];
    case 'details': return row.details;
    default: return null;
  }
}

function haSortValue(member: HaMember, key: string): SortValue {
  switch (key) {
    case 'member': return member.hostname;
    // the primary first
    case 'role': return member.is_master ? 0 : 1;
    case 'cpu': return member.cpu;
    case 'mem': return member.mem;
    case 'sessions': return member.sessions;
    default: return null;
  }
}

function sensorSortValue(sensor: SensorEntry, key: string): SortValue {
  if (key === 'name') {
    return sensor.name;
  }
  // state column: the alarms first
  return sensor.alarm ? 0 : 1;
}

function adminSortValue(admin: AdminEntry, key: string): SortValue {
  switch (key) {
    case 'name': return admin.name;
    case 'from': return admin.ip;
    case 'since': {
      if (admin.login_time == null) return null;
      // The column shows how long ago the admin logged in. The newest
      // login has the smallest age but the biggest login time: the minus
      // sign turns one order into the other.
      return -admin.login_time;
    }
    default: return null;
  }
}

function ntpSortValue(server: NtpServer, key: string): SortValue {
  switch (key) {
    case 'server': return server.server;
    // the unreachable servers first
    case 'state': return server.reachable;
    case 'stratum': return server.stratum;
    case 'offset': {
      if (server.offset_ms == null) return null;
      // how far the clock is, ahead or behind: the sign doesn't matter
      return Math.abs(server.offset_ms);
    }
    case 'delay': return server.delay_ms;
    default: return null;
  }
}

function pollSortValue(item: PollRowVm, key: string): SortValue {
  const row = item.row;
  switch (key) {
    // the problems first (see POLL_RANK)
    case 'status': return POLL_RANK[row.verdict];
    case 'name': return row.name;
    case 'last': return row.last_status;
    case 'polls': {
      if (!row.polled) return null;
      // the share of good answers: the worst endpoints first when ascending
      return row.ok / row.polled;
    }
    case 'last_ok': return row.last_ok;
    default: return null;
  }
}

function certSortValue(cert: CertEntry, key: string): SortValue {
  if (key === 'name') {
    return cert.name;
  }
  // expires column: the days left (negative when expired)
  return cert.days_left;
}

/** The little arrow next to a KPI: how much it moved since the reading
 *  before. null when there's nothing worth showing. */
function trendOf(
  now: number | null | undefined,
  previous: number | null | undefined,
  formatter: Formatter,
  upIsBad = false,
): Trend | null {
  if (now == null || previous == null || previous === 0) return null;
  const changePct = ((now - previous) / previous) * 100;
  // less than half a percent is just noise
  if (Math.abs(changePct) < 0.5) return null;

  let cls: Trend['cls'];
  if (changePct > 0) {
    cls = upIsBad ? 'up-bad' : 'up';
  } else {
    cls = 'down';
  }
  return {
    cls,
    arrow: changePct > 0 ? '▲' : '▼',
    pct: Math.abs(changePct).toFixed(0),
    title: `vs previous sample (${formatter(previous)})`,
  };
}

const CHECK_BADGE: Record<CheckStatus, Badge> = {
  pass: { cls: 'label-success', text: 'pass' },
  note: { cls: 'label-info', text: 'note' },
  ack: { cls: 'label-info', text: 'ack' },
  warn: { cls: 'label-warning', text: 'warn' },
  fail: { cls: 'label-danger', text: 'fail' },
};
const CHECK_RANK: Record<CheckStatus, number> = {
  fail: 0, warn: 1, ack: 2, note: 3, pass: 4,
};

/* ============================================================================
 * Data service
 * ========================================================================== */

@Injectable({ providedIn: 'root' })
export class FortinetDashboardService {
  constructor(private http: HttpClient) {}

  /** @param apiBase  Base URL of the Fortinet routes of the NetScope API
   *                  (default: environment.fortinetApiUrl, e.g. '/api/fortinet').
   *
   *  Returns the cold Observable so the caller can cancel it (switchMap
   *  cancels the running request when a new refresh starts). */
  getDocument(range?: string | null, serial?: string | null,
              apiBase = environment.fortinetApiUrl): Observable<DashboardDocument> {
    let params = new HttpParams();
    if (range) {
      params = params.set('range', range);
    }
    if (serial) {
      params = params.set('serial', serial);
    }
    return this.http.get<DashboardDocument>(`${apiBase}/data.json`, { params });
  }

  /** Acknowledges an issue (acked=true) or brings it back (acked=false).
   *  Same base URL as getDocument. */
  setIssueAck(serial: string, issue: Issue, acked: boolean,
              apiBase = environment.fortinetApiUrl, extra?: { note?: string; by?: string }): Observable<unknown> {
    // note / by: only sent when given, '' clears them
    const body = {
      serial, acked,
      issue: { id: issue.id, sev: issue.sev, title: issue.title,
               detail: issue.detail },
      ...(extra ?? {}),
    };
    return this.http.post(`${apiBase}/issues/ack`, body);
  }
}

/* ============================================================================
 * Component
 * ========================================================================== */

/** Picker value of the "All devices" entry. Defined in fortinet-shared, so
 *  the application shell can use it without importing this component. */
export const FLEET_VIEW = FORTINET_FLEET_VIEW;

/** Where this browser remembers the last device, window and tab, for when
 *  the page is opened without them in the URL. */
const LAST_VIEW_KEY = 'fgLastView';

/** URL query keys, with a prefix so they don't clash with the host app. */
const Q_DEVICE = 'fgDevice';
const Q_RANGE = 'fgRange';
const Q_TAB = 'fgTab';
const Q_ALL = 'all';

/** Put on <body> while the report prints, so the rules at the end of the
 *  stylesheet can hide the app shell around the component. */
const PRINT_CLASS = 'fgt-printing';

/** Page widths (in px, the page without the NetScope menu) below which the
 *  cards are stacked one per row (fg-narrow), and the page loses some of its
 *  padding (fg-phone). See applyWidthClasses. */
const NARROW_PAGE_WIDTH = 860;
const PHONE_PAGE_WIDTH = 600;

/** Rows shown by each policy table before "show all". */
const POLICY_ROWS = 10;
/** Events listed before "show all". */
const EVENT_ROWS = 40;
/** Interfaces listed in the printed report and the share summary (the
 *  busiest ones; the page table lists every interface). */
const REPORT_IFACE_ROWS = 10;
/** Devices above which the picker gets a filter box. */
const PICKER_FILTER_MIN = 8;

/** cds-icon shape of each tab in the shell's vertical navigation. I can
 *  only use shapes the shell registers with ClarityIcons. */
const TAB_ICONS: Record<DeviceTab, string> = {
  overview: 'dashboard',
  traffic: 'two-way-arrows',
  // 'tree-view' must be registered in the shell like the others
  map: 'tree-view',
  interfaces: 'network-switch',
  policies: 'checkbox-list',
  vpn: 'world',
  system: 'cog',
};

/** Sections of the all-devices page, in page order. The ids match the
 *  `fleet-<id>` anchors of fortinet-fleet.component.html. */
const FLEET_SECTIONS: { id: string; label: string; icon: string }[] = [
  { id: 'top', label: 'Overview', icon: 'dashboard' },
  { id: 'devices', label: 'Devices', icon: 'organization' },
  { id: 'issues', label: 'Issues', icon: 'exclamation-triangle' },
  { id: 'licenses', label: 'Licenses, certificates', icon: 'note' },
  { id: 'events', label: 'Recent events', icon: 'calendar' },
  { id: 'sdwan', label: 'SD-WAN health', icon: 'world' },
  { id: 'hygiene', label: 'Policy cleanup', icon: 'trash' },
];

const TABS: { id: DeviceTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'traffic', label: 'Traffic' },
  { id: 'map', label: 'Network map' },
  { id: 'interfaces', label: 'Interfaces' },
  { id: 'policies', label: 'Policies' },
  { id: 'vpn', label: 'VPN, WAN & routing' },
  { id: 'system', label: 'System' },
];

@Component({
  selector: 'app-fortinet-dashboard',
  standalone: true,
  imports: [CommonModule, FmtBpsPipe, FmtBytesPipe, FmtNumPipe, FmtPctPipe,
            IssueBadgePipe, FortinetFleetComponent, FortinetInterfacesComponent,
            FortinetNetmapComponent, ClrSpinnerModule, ClrProgressBarModule],
  templateUrl: './fortinet-dashboard.component.html',
  styleUrls: ['./fortinet-dashboard.component.css'],
  // Every field the template uses is set in one place (render), so OnPush
  // plus an explicit markForCheck keeps change detection down to the real
  // updates.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FortinetDashboardComponent implements OnInit, OnDestroy {
  /** Browser auto-refresh interval in ms, 0 turns it off. The refreshes
   *  pause while the browser tab is hidden. */
  @Input() refreshMs = 60_000;

  /** Base URL of the Fortinet routes of the NetScope API (see FortinetDashboardService). */
  @Input() apiBase = environment.fortinetApiUrl;

  /** Opens on the all-devices overview instead of the default device. */
  @Input() startWithFleet = false;

  /** Keeps the device, window and tab in the URL query. */
  @Input() syncUrl = true;

  /** The application shell draws the device picker and the page sections
   *  in its vertical navigation (see fortinet-shared.FortinetNavService).
   *  Setting it to false brings back the component's own header and tab
   *  strip, e.g. when the page is embedded somewhere without a shell. */
  @Input() navInShell = true;

  @ViewChild(FortinetFleetComponent) private fleet?: FortinetFleetComponent;

  // ---- header / status ----
  serial = '-';
  version = '-';
  statusText = 'Loading…';
  statusErr = false;
  deviceInfo: DeviceInfo | null = null;
  /** The groups of the device shown in the header, main group first. Each
   *  one is a link to the "All devices" page filtered on that group. */
  headerGroups: { name: string; text: string; main: boolean }[] = [];
  /** The group the "All devices" page must open on, after a click on a
   *  group of the header (passed to its initialGroupFilter input) */
  pendingFleetGroup: string | null = null;
  /** "Last poll 17 min ago" when the data is stale, otherwise '' */
  staleText = '';
  /** Title of the device in the header, report and network map. */
  deviceTitle = '-';

  /** Auto-refresh paused by the header button. Manual refreshes and
   *  navigation still fetch. */
  paused = false;

  // ---- history-range toolbar ----
  ranges: HistoryRange[] = [];
  histRange: string | null = null; // set from meta.history_range on first load
  /** lines are bucket averages drawn with a min/max band (meta.bucketed) */
  bucketed = false;
  /** The window I clicked and whose document hasn't arrived yet (null when
   *  nothing is loading). The button shows a spinner, and the text next
   *  to the buttons counts the seconds, so a click is never silent. */
  pendingRange: string | null = null;
  /** seconds since the click, shown while loading */
  pendingSeconds = 0;
  private pendingStartedAt = 0;
  private pendingTimer: ReturnType<typeof setInterval> | null = null;
  /** "7 d loaded", shown a few seconds after the document arrived */
  rangeDoneText = '';
  private rangeDoneTimer: ReturnType<typeof setTimeout> | null = null;
  /** Which sessions the traffic breakdown and the network map were built
   *  from: "Sessions of 2026-09-29 08:42 to 2026-09-29 15:10, 78 polls". */
  coverageText = '';
  /** the budget cut the window short (see TrafficBucket.budget_cut) */
  coverageCut = false;

  // ---- device picker ----
  serials: string[] = [];
  devices: DeviceRef[] = [];
  pickerFilter = '';
  pickerDevices: DeviceRef[] = [];
  pickerGroups: PickerGroup[] = [];
  selectedSerial: string | null = null; // set from meta.serial on first load
  /** true while the all-devices overview replaces the device page */
  fleetMode = false;
  readonly fleetId = FLEET_VIEW;

  // ---- tabs ----
  readonly tabs = TABS;
  tab: DeviceTab = 'overview';
  readonly tabIds = 'dev-' + Math.random().toString(36).slice(2, 8);

  // ---- template-bound view models ----
  thresholds: Thresholds = thresholdsOf();
  kpis: KpiVm[] = [];
  checkGroups: CheckGroupVm[] = [];
  checksSummary = '';
  issues: Issue[] = [];
  issuesAcked: Issue[] = [];
  /** false with an older backend: no checkboxes then */
  ackSupported = false;
  /** issue ids with a request in flight (their checkbox is disabled) */
  ackPending = new Set<string>();
  issuesLoaded = false;

  /** every interface of the device, busiest first (document iface_latest) */
  ifaceRows: IfaceLatest[] = [];
  /** the busiest REPORT_IFACE_ROWS of them, for the printed report and the
   *  share summary, which must stay short */
  reportIfaceRows: IfaceLatest[] = [];
  /** "Interfaces", or "Interfaces (10 busiest of 42)" when some are left out */
  reportIfaceTitle = 'Interfaces';
  ifaceFilter = '';
  ifaceShown: IfaceLatest[] = [];
  /** Default: the order the backend sent. The numbers start with the
   *  biggest (busiest, most errors), name and link start ascending (down
   *  links first). */
  readonly ifaceSort = new TableSort(null, true, ['rx', 'tx', 'util', 'errors']);
  /** The Duplex column only shows when an interface is in half duplex:
   *  otherwise it would be a column of "full" in a narrow card. With it,
   *  the card takes 5 grid columns instead of 4. */
  ifaceHasHalfDuplex = false;
  /** Errors cell per interface name (see IfaceErrVm) */
  ifaceErr: Record<string, IfaceErrVm | undefined> = {};

  /** Policies hit at least once, by current throughput. */
  policyUsedRows: PolicyTotal[] = [];
  /** The zero-hit policies when there are some, otherwise the fewest hits. */
  policyLessUsedRows: PolicyTotal[] = [];
  lessUsedShowsUnused = false;
  readonly policyRowsCollapsed = POLICY_ROWS;
  showAllUsedPolicies = false;
  showAllLessUsedPolicies = false;
  policyFilter = '';
  policyUsedMatches = 0;
  policyLessUsedMatches = 0;
  policyUsedShown: PolicyTotal[] = [];
  policyLessUsedShown: PolicyTotal[] = [];
  /** Busiest first by default. The table is sorted before being cut to its
   *  first rows, like the pages of a datagrid. */
  readonly policyUsedSort = new TableSort('now', false, ['now', 'sessions', 'hits', 'total']);
  /** Default: the order of render (by name, or by fewest hits). Every
   *  column starts with the smallest value: it's the "less used" table. */
  readonly policyLessUsedSort = new TableSort();
  oversizeNames = new Set<string>();
  /** "Top destination ports" ranking mode, set by the header toggle. */
  dportsByVolume = false;

  // ---- device health / VPN / WAN ----
  firmware: FirmwareInfo | null = null;
  licenseActive: LicenseEntry[] = [];
  licenseInactive: LicenseEntry[] = [];
  showInactiveLicenses = false;
  sensorRows: SensorEntry[] = [];
  vpnTunnels: VpnTunnel[] = [];
  sdwanRows: SdwanMember[] = [];
  // the same lists, sorted like their table (see updateSmallTables)
  licenseActiveShown: LicenseEntry[] = [];
  licenseInactiveShown: LicenseEntry[] = [];
  sensorShown: SensorEntry[] = [];
  tunnelsShown: VpnTunnel[] = [];
  sdwanShown: SdwanMember[] = [];
  haShown: HaMember[] = [];
  adminShown: AdminEntry[] = [];
  ntpShown: NtpServer[] = [];
  routingShown: RoutingRowVm[] = [];
  // Column sort of each table. The active and inactive licenses share one
  // (the inactive ones are the end of the same list).
  readonly licenseSort = new TableSort();
  readonly sensorSort = new TableSort();
  readonly tunnelSort = new TableSort(null, true, ['now', 'total']);
  readonly sdwanSort = new TableSort(null, true, ['latency', 'jitter', 'loss']);
  readonly haSort = new TableSort(null, true, ['cpu', 'mem', 'sessions']);
  readonly adminSort = new TableSort();
  readonly ntpSort = new TableSort(null, true, ['offset', 'delay']);
  readonly routingSort = new TableSort();
  /** soonest expiry first, the order dashboard.py sends */
  readonly certSort = new TableSort('expires', true);
  hasSdwan = false;
  hasTunnels = false;
  hasSslUsers = false;
  hasIfaceErrors = false;
  hasWanVpn = false;
  /** Network map tab: document `session_traffic.bucket.netmap` */
  netmap: NetmapDoc | null = null;
  /** the saved state of the map (document `netmap_state`), and the device
   *  it belongs to */
  netmapState: NetmapState | null = null;
  private netmapStateSerial: string | null = null;
  /** Interfaces tab: document `ports` */
  ports: PortsDoc | null = null;
  hasPorts = false;
  haMembers: HaMember[] = [];
  fortiguard: FortiguardInfo | null = null;
  adminRows: AdminEntry[] = [];
  /** a document carrying the health block has arrived (newer backends) */
  hasHealth = false;

  // ---- time, certificates, routing, events ----
  systemState: SystemState | null = null;
  ntp: NtpInfo | null = null;
  certRows: CertEntry[] = [];
  /** factory certificates listed too (they are folded by default) */
  showBuiltinCerts = false;
  certsShown: CertEntry[] = [];
  bgpRows: BgpPeer[] = [];
  ospfRows: OspfPeer[] = [];

  // ---- Endpoint polls card (System tab, document `poll_report`) ----
  pollReport: PollReport | null = null;
  /** every row, in the order of endpoints.py (see buildPollRows) */
  private pollRows: PollRowVm[] = [];
  /** the rows of the chosen filter, sorted like the table */
  pollShown: PollRowVm[] = [];
  readonly pollFilters = POLL_FILTERS;
  pollFilter: PollFilter = 'all';
  /** how many rows each filter button would show */
  pollFilterCounts: Record<PollFilter, number> = { all: 0, look: 0, fine: 0, ok: 0 };
  /** the line in the card header: "18 OK, 5 not a problem, 1 to look at" */
  pollSummary = '';
  /** the newest poll first when the "Last success" column is clicked */
  readonly pollSort = new TableSort(null, true, ['last_ok']);

  /** bgpRows and ospfRows in one list, for the routing table */
  private routingRows: RoutingRowVm[] = [];
  hasRouting = false;
  events: FortiEvent[] = [];
  eventsTotal = 0;
  eventKinds: EventKind[] = [];
  eventKind: EventKind | 'all' = 'all';
  showAllEvents = false;
  eventsShown: FortiEvent[] = [];
  eventsMatching = 0;
  /** px: one row per kind on the dot chart, so every row keeps its label */
  eventsChartHeight = 120;
  readonly eventRowsCollapsed = EVENT_ROWS;
  readonly eventKindLabel = EVENT_KIND_LABEL;
  /** the inline note form of an acknowledged issue */
  readonly noteEd: AckNoteEditor;

  // ---- report and share ----
  reportMode = false;
  reportAt = '';
  shareOpen = false;
  shareText = '';
  shareLeaks: string[] = [];
  shareReplaced = 0;
  shareStatus = '';

  ackNote = ackNote;
  ackNoteLine = ackNoteLine;
  firstSeenText = firstSeenText;
  firstSeenTitle = firstSeenTitle;
  eventBadge = eventBadge;
  eventWhen = eventWhen;
  fmtAge = fmtAge;
  fmtStamp = fmtStamp;

  /** Which tab of the issues card is shown. */
  issuesTab: 'open' | 'acked' = 'open';
  readonly tabIdPrefix = 'issues-' + Math.random().toString(36).slice(2, 8);

  private kit: ChartKit;
  private readonly reload$ = new Subject<void>();
  private readonly destroyed$ = new Subject<void>();
  private destroyed = false;
  private lastDoc: DashboardDocument | null = null;
  private receivedAt = 0;
  private lastLoadedAt = 0;
  private themeObserver: MutationObserver | null = null;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private stopWatchingWidth: (() => void) | null = null;
  private chartsTimer: ReturnType<typeof setTimeout> | null = null;
  /** resize every chart after the next pass (the report was shown) */
  private resizeAfterCharts = false;
  /** issue id to show once the next document arrives (fleet "Show") */
  private pendingEvidence: string | null = null;
  /** Section of the all-devices page the navigation points at. */
  private navSection = FLEET_SECTIONS[0].id;
  /** Last known state of every device, kept from the fleet document so
   *  the picker can still show its dots while a device page is open. */
  private navStates = new Map<string, NavSev>();
  /** The URL gave the history window (a bookmark or a shared link): on
   *  arrival it wins over the application's time selector. */
  private rangeFromUrl = false;
  private nav = inject(FortinetNavService);
  private location = inject(Location, { optional: true });
  private afterPrint = () => this.endReport();

  constructor(
    private svc: FortinetDashboardService,
    private hostRef: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.kit = new ChartKit(hostRef.nativeElement, zone);
    this.noteEd = new AckNoteEditor(inject(FORTINET_USER, { optional: true }));
  }

  // ------------------------------------------------------------- lifecycle
  ngOnInit(): void {
    this.fleetMode = this.startWithFleet;
    this.readUrl();

    // One pipeline for the periodic poll and the manual refreshes.
    // switchMap cancels a request still running when a newer one starts.
    // In fleet mode the embedded fleet component polls on its own.
    const triggers$ = merge(
      pollTriggers(this.refreshMs, () => this.lastLoadedAt, () => this.paused),
      this.reload$,
    );
    triggers$
      .pipe(
        switchMap(() => {
          if (this.fleetMode) {
            return EMPTY;
          }
          this.lastLoadedAt = Date.now();
          return this.svc
            .getDocument(this.histRange, this.selectedSerial, this.apiBase)
            .pipe(catchError((error: unknown) => {
              // I keep the error in here: if it reached the outer stream,
              // the refreshes would stop for good
              this.onError(error);
              return EMPTY;
            }));
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((data) => this.onDocument(data));

    // What the user picks in the shell's vertical navigation.
    this.nav.commands$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((command) => this.onNavCommand(command));
    this.publishNav();

    // The chart colours come from CSS variables, so a theme switch only
    // needs the charts redrawn.
    // Ignored while the report is shown: the print class this component
    // puts on <body> would trigger it, and the charts are hidden then.
    this.themeObserver = new MutationObserver(() => {
      if (this.lastDoc && !this.destroyed && !this.fleetMode && !this.reportMode) {
        this.scheduleCharts();
      }
    });
    this.themeObserver.observe(document.body, {
      attributes: true, attributeFilter: ['cds-theme', 'class'],
    });
    window.addEventListener('afterprint', this.afterPrint);

    // the layout and the charts follow the width of the page (see ChartKit.watchWidth)
    this.applyWidthClasses();
    this.stopWatchingWidth = this.kit.watchWidth(this.hostRef.nativeElement, () => this.onPageWidthChange());
  }

  private onPageWidthChange(): void {
    this.applyWidthClasses();
    this.onResize();
  }

  /** Puts the classes of the page width on this component's element:
   *  fg-narrow (one card per row) and fg-phone (less padding). The CSS reads
   *  them with :host-context(), in this component and in the fleet,
   *  interfaces and network map components.
   *  Why not a CSS @media rule: it looks at the WINDOW, and the page is the
   *  window minus the NetScope menu (240 px open, 48 px collapsed), so the
   *  cards stayed side by side, squeezed, while the menu was open. */
  private applyWidthClasses(): void {
    const host = this.hostRef.nativeElement;
    const width = host.clientWidth;
    host.classList.toggle('fg-narrow', width < NARROW_PAGE_WIDTH);
    host.classList.toggle('fg-phone', width < PHONE_PAGE_WIDTH);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopWatchingWidth?.();
    // the shell puts its own menu back
    this.nav.clear();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.reload$.complete();
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    if (this.chartsTimer) {
      clearTimeout(this.chartsTimer);
    }
    if (this.pendingTimer) {
      clearInterval(this.pendingTimer);
    }
    if (this.rangeDoneTimer) {
      clearTimeout(this.rangeDoneTimer);
    }
    this.themeObserver?.disconnect();
    document.body.classList.remove(PRINT_CLASS);
    window.removeEventListener('afterprint', this.afterPrint);
    this.kit.dispose();
  }

  /** Called by the width watcher started in ngOnInit (window resized, or the
   *  shell menu collapsed / opened). */
  onResize(): void {
    // Debounced and outside Angular: resizing a dozen charts on every frame
    // of a window drag makes the page stutter.
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.zone.runOutsideAngular(() => {
      this.resizeTimer = setTimeout(() => {
        this.resizeTimer = null;
        // The print preview resizes the window while the charts are hidden:
        // resizing them then would leave them 0 px wide. The end of the
        // report resizes them instead.
        if (!this.destroyed && !this.reportMode) {
          this.kit.resize();
        }
      }, 120);
    });
  }

  // --------------------------------------------------------------- URL
  private readUrl(): void {
    if (!this.syncUrl || !this.location) return;
    const queryString = this.location.path().split('?')[1] ?? '';
    const query = new URLSearchParams(queryString);

    // Nothing in the URL (a plain link to the page): I go back to where
    // this browser was last time. A URL with a view (bookmark, shared
    // link) always wins over that.
    if (!query.get(Q_DEVICE) && !query.get(Q_RANGE) && !query.get(Q_TAB)) {
      this.readLastView();
      return;
    }

    const device = query.get(Q_DEVICE);
    if (device === Q_ALL) {
      this.fleetMode = true;
    } else if (device) {
      this.fleetMode = false;
      this.selectedSerial = device;
    }

    const range = query.get(Q_RANGE);
    if (range) {
      this.histRange = range;
      this.rangeFromUrl = true;
    }

    const tab = query.get(Q_TAB) as DeviceTab | null;
    if (tab && TABS.some((knownTab) => knownTab.id === tab)) {
      this.tab = tab;
    }
  }

  private writeUrl(): void {
    if (!this.syncUrl || !this.location) return;
    const [path, queryString] = this.location.path().split('?');
    const query = new URLSearchParams(queryString ?? '');

    // an empty value removes the key from the URL
    const setOrDelete = (key: string, value: string | null) => {
      if (value) {
        query.set(key, value);
      } else {
        query.delete(key);
      }
    };
    setOrDelete(Q_DEVICE, this.fleetMode ? Q_ALL : this.selectedSerial);
    setOrDelete(Q_RANGE, this.histRange);
    // the overview is the default tab, no need to write it
    setOrDelete(Q_TAB, this.fleetMode || this.tab === 'overview' ? null : this.tab);
    this.location.replaceState(path, query.toString());
    this.saveLastView();
  }

  /** The last view of this browser (device, window, tab), if any. */
  private readLastView(): void {
    let saved: { device?: string; range?: string; tab?: string } | null = null;
    try {
      saved = JSON.parse(localStorage.getItem(LAST_VIEW_KEY) ?? 'null');
    } catch {
      // no storage, or something unreadable in it: I start as usual
      return;
    }
    if (!saved || typeof saved !== 'object') return;

    if (saved.device === Q_ALL) {
      this.fleetMode = true;
    } else if (typeof saved.device === 'string' && saved.device) {
      this.fleetMode = false;
      this.selectedSerial = saved.device;
    }
    // Not marked as coming from the URL: when the page opens, the
    // application's time selector still decides the window, as usual.
    if (typeof saved.range === 'string' && saved.range) {
      this.histRange = saved.range;
    }
    const tab = saved.tab as DeviceTab | undefined;
    if (tab && TABS.some((knownTab) => knownTab.id === tab)) {
      this.tab = tab;
    }
  }

  private saveLastView(): void {
    const view = {
      device: this.fleetMode ? Q_ALL : this.selectedSerial,
      range: this.histRange,
      tab: this.tab,
    };
    try {
      localStorage.setItem(LAST_VIEW_KEY, JSON.stringify(view));
    } catch {
      // no storage (private mode, blocked): nothing to remember
    }
  }

  // ------------------------------------------------------------- actions
  /** Asks for a new document (cancels one still running). */
  refresh(): void {
    if (!this.destroyed) {
      this.reload$.next();
    }
  }

  /** Header refresh button. */
  refreshNow(): void {
    if (this.fleetMode) {
      this.fleet?.reload();
    } else {
      this.refresh();
    }
  }

  /** Header button: pauses or resumes the automatic refresh. Resuming
   *  fetches right away when the data is older than one interval. */
  togglePause(): void {
    this.paused = !this.paused;
    // the fleet page resumes by itself (its `paused` input)
    if (!this.paused && !this.fleetMode
        && Date.now() - this.lastLoadedAt >= this.refreshMs) {
      this.refresh();
    }
    let text = this.statusText.replace(/ \(auto-refresh paused\)$/, '');
    if (this.paused) {
      text += ' (auto-refresh paused)';
    }
    this.statusText = text;
    this.cdr.markForCheck();
  }

  setRange(id: string): void {
    if (this.histRange === id) return;
    this.histRange = id;
    this.kit.forgetState('zoom');
    this.writeUrl();
    this.startPendingRange(id);
    this.refresh();
  }

  /** The label of a window ("7 d"), or its id when the list isn't there yet. */
  rangeLabel(id: string | null): string {
    const range = this.ranges.find((item) => item.id === id);
    if (range) {
      return range.label;
    }
    return id ?? '';
  }

  /** A window was clicked: the spinner and the seconds counter start. */
  private startPendingRange(id: string): void {
    this.pendingRange = id;
    this.pendingSeconds = 0;
    this.pendingStartedAt = Date.now();
    this.rangeDoneText = '';
    if (this.rangeDoneTimer) {
      clearTimeout(this.rangeDoneTimer);
      this.rangeDoneTimer = null;
    }
    // One tick per second for the counter. I clear it before starting a
    // new one, in case I click a second window while the first loads.
    if (this.pendingTimer) {
      clearInterval(this.pendingTimer);
    }
    this.pendingTimer = setInterval(() => {
      this.pendingSeconds = Math.round((Date.now() - this.pendingStartedAt) / 1000);
      this.cdr.markForCheck();
    }, 1000);
    this.cdr.markForCheck();
  }

  /** The document of the clicked window arrived (ok) or failed (not ok). */
  private finishPendingRange(ok: boolean): void {
    if (!this.pendingRange) return;
    const label = this.rangeLabel(this.pendingRange);
    this.pendingRange = null;
    if (this.pendingTimer) {
      clearInterval(this.pendingTimer);
      this.pendingTimer = null;
    }
    if (!ok) {
      // the error itself is in the status text
      this.cdr.markForCheck();
      return;
    }
    // A short confirmation, even when the answer came from the cache in a
    // few milliseconds and the spinner was hardly visible.
    this.rangeDoneText = `${label} loaded`;
    this.rangeDoneTimer = setTimeout(() => {
      this.rangeDoneText = '';
      this.rangeDoneTimer = null;
      this.cdr.markForCheck();
    }, 3000);
    this.cdr.markForCheck();
  }

  /** coverageText and coverageCut, from the traffic breakdown. */
  private buildCoverage(bucket: TrafficBucket | null | undefined): void {
    this.coverageText = '';
    this.coverageCut = false;
    if (!bucket || bucket.t_start == null || bucket.t_end == null) {
      return;
    }
    const pollWord = bucket.polls === 1 ? 'poll' : 'polls';
    this.coverageText = `Sessions of ${fmtStamp(bucket.t_start)} to ${fmtStamp(bucket.t_end)}, `
      + `${fmtNum(bucket.polls)} ${pollWord}`;
    this.coverageCut = !!bucket.budget_cut;
  }

  /** Why a tab can't be opened, or '' when it can. Only the VPN & WAN and
   *  Interfaces tabs can be empty, judged once a document has arrived. */
  tabOffReason(tab: DeviceTab): string {
    if (!this.issuesLoaded) return '';
    if (tab === 'vpn' && !this.hasWanVpn) {
      return 'No SD-WAN health checks, IPsec tunnels, SSL-VPN users or routing neighbors in this window';
    }
    if (tab === 'map' && !this.netmap) {
      return 'No session details in this window yet';
    }
    if (tab === 'interfaces' && !this.hasPorts) {
      return 'The collector has not read the interface configuration of this device yet';
    }
    return '';
  }

  setTab(tab: DeviceTab, focus = false): void {
    if (this.tabOffReason(tab)) return;
    if (this.tab !== tab) {
      this.tab = tab;
      this.writeUrl();
      this.cdr.markForCheck();
      // render the new tab now, so its chart containers and evidence
      // anchors exist before the charts and the highlight look for them
      if (!this.destroyed) {
        this.cdr.detectChanges();
      }
      this.scheduleCharts();
    }
    if (focus) {
      setTimeout(() => this.kit.el(`${this.tabIds}-${tab}`)?.focus());
    }
    this.publishNav();
  }

  /** Arrow keys, Home and End on the tab list (the focus follows). */
  onTabKey(event: KeyboardEvent): void {
    // the greyed-out tabs are skipped
    const openTabs = TABS.filter((tab) => !this.tabOffReason(tab.id));
    const currentIndex = openTabs.findIndex((tab) => tab.id === this.tab);
    const count = openTabs.length;

    let nextIndex = -1;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % count;
    } else if (event.key === 'ArrowLeft') {
      // + count so it wraps around instead of going negative
      nextIndex = (currentIndex - 1 + count) % count;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = count - 1;
    }
    if (nextIndex < 0 || !count) return;
    event.preventDefault();
    this.setTab(openTabs[nextIndex].id, true);
  }

  setSerial(serial: string): void {
    if (serial === FLEET_VIEW) {
      if (this.fleetMode) return;
      this.fleetMode = true;
      this.statusText = 'Loading all devices…';
      this.statusErr = false;
      this.navSection = FLEET_SECTIONS[0].id;
      this.closeShare();
      this.writeUrl();
      this.publishNav();
      this.refresh();          // cancels a device request still running
      this.cdr.markForCheck();
      return;
    }

    if (!this.fleetMode && this.selectedSerial === serial) return;
    const leavingFleet = this.fleetMode;
    this.fleetMode = false;
    this.selectedSerial = serial;
    this.statusText = 'Loading ' + this.labelOf(serial) + '…';
    this.statusErr = false;
    this.kit.forgetState('all');
    this.closeShare();
    if (leavingFleet) {
      // the device cards are recreated empty until the document arrives
      this.lastDoc = null;
      this.issuesLoaded = false;
      this.issuesAcked = [];
      this.issuesTab = 'open';
    }
    this.writeUrl();
    this.publishNav();
    this.cdr.markForCheck();
    this.refresh();
  }

  /* ------------------------------------------------- shell navigation ----
   * The device picker and the page sections are drawn by the application
   * shell (dcnetscope-main.component) in its vertical navigation. This
   * component publishes what they show and does what they ask.
   * ------------------------------------------------------------------- */

  /** A command from the shell's vertical navigation. */
  private onNavCommand(command: NavCommand): void {
    if (this.destroyed) return;
    switch (command.kind) {
      case 'device':
        this.setSerial(command.serial);
        break;
      case 'tab':
        this.setTab(command.tab);
        break;
      case 'section':
        this.goToFleetSection(command.id);
        break;
      case 'group':
        this.setFleetGroup(command.name);
        break;
      case 'range':
        this.followShellRange(command.id, command.arrival);
        break;
    }
  }

  /** The History window follows the application's time selector: on
   *  arrival (unless the URL gave a window) and on every later change. An
   *  id this backend doesn't offer is ignored. */
  private followShellRange(id: string, arrival: boolean): void {
    if (arrival && this.rangeFromUrl) return;
    if (this.ranges.length && !this.ranges.some((range) => range.id === id)) return;
    this.setRange(id);
    this.cdr.markForCheck();
  }

  /** The fleet page filtered itself (its own group rows, search, state):
   *  the navigation follows its group and its counts. */
  onFleetFilters(_filters: FleetFilters): void {
    if (this.fleetMode) {
      this.publishNav();
    }
  }

  /** All-devices page: scrolls a section into view (the cards have
   *  id="fleet-<id>"). */
  private goToFleetSection(id: string): void {
    if (!this.fleetMode) return;
    this.navSection = id;
    const element = this.hostRef.nativeElement
      .querySelector<HTMLElement>('#fleet-' + CSS.escape(id));
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.publishNav();
    this.cdr.markForCheck();
  }

  /** A group of the device header was clicked: opens the "All devices" page
   *  showing only the devices of that group (and of the groups inside it). */
  openGroupOnFleet(groupName: string): void {
    if (this.fleetMode) {
      this.setFleetGroup(groupName);
      return;
    }
    // the fleet page doesn't exist yet: it reads this when it is created
    this.pendingFleetGroup = groupName;
    this.setSerial(FLEET_VIEW);
  }

  /** All-devices page: focuses the page on one group (again: every group). */
  private setFleetGroup(name: string | null): void {
    const fleet = this.fleet;
    if (!this.fleetMode || !fleet) return;
    fleet.setGroupFilter(fleet.groupFilter === name ? null : name);
    this.goToFleetSection('devices');
  }

  /** Everything the navigation shows, rebuilt from the current state. */
  private publishNav(): void {
    if (this.destroyed) return;
    const info = this.deviceInfo;
    let mainGroupText = '';
    if (info) {
      const mainGroup = mainGroupOf(groupsOfDevice(info));
      if (mainGroup) {
        mainGroupText = groupPathText(mainGroup);
      }
    }
    const subParts = [mainGroupText, info?.host || ''];
    const deviceSub = subParts.filter((part) => !!part).join(', ');

    let deviceLabel: string;
    if (this.fleetMode) {
      deviceLabel = 'All devices';
    } else if (this.deviceTitle && this.deviceTitle !== '-') {
      deviceLabel = this.deviceTitle;
    } else {
      deviceLabel = 'Device';
    }

    this.nav.patch({
      active: true,
      fleetMode: this.fleetMode,
      devices: this.navDevices(),
      groups: this.navGroups(),
      groupFilter: this.fleet?.groupFilter ?? null,
      selectedSerial: this.selectedSerial,
      deviceLabel,
      deviceSub: this.fleetMode ? this.navFleetSub() : deviceSub,
      deviceSev: this.fleetMode ? 'unknown' : this.navSevOf(this.selectedSerial || ''),
      current: this.fleetMode ? this.navSection : this.tab,
      sections: this.navSections(),
      fleetSections: this.navFleetSections(),
    });
  }

  private navDevices(): NavDevice[] {
    const result: NavDevice[] = [];
    for (const device of this.devices) {
      result.push({
        serial: device.serial,
        text: this.deviceText(device),
        label: device.label || device.serial,
        groups: groupsOfDevice(device),
        sev: this.navSevOf(device.serial),
      });
    }
    return result;
  }

  private navGroups(): NavGroup[] {
    const result: NavGroup[] = [];
    for (const group of this.fleet?.groups ?? []) {
      result.push({ name: group.name, label: group.label, depth: group.depth, devices: group.devices });
    }
    return result;
  }

  /** "12 devices, 2 to look at" under the switcher. */
  private navFleetSub(): string {
    const deviceCount = this.serials.length;
    let toLookAt = 0;
    for (const sev of this.navStates.values()) {
      if (sev === 'danger' || sev === 'warning' || sev === 'offline') {
        toLookAt++;
      }
    }
    if (!deviceCount) return '';
    let text = `${deviceCount} device${deviceCount > 1 ? 's' : ''}`;
    if (toLookAt) {
      text += `, ${toLookAt} to look at`;
    }
    return text;
  }

  /** Dot next to a device: its own issues when it's the open device,
   *  otherwise the state from the last fleet document. */
  private navSevOf(serial: string): NavSev {
    if (serial && serial === this.selectedSerial && !this.fleetMode && this.issuesLoaded) {
      if (this.deviceInfo?.stale) return 'offline';
      if (this.issues.some((issue) => issue.sev === 'danger')) return 'danger';
      if (this.issues.some((issue) => issue.sev === 'warning')) return 'warning';
      return 'ok';
    }
    return this.navStates.get(serial) ?? 'unknown';
  }

  /** The state from the fleet table, as a dot colour. */
  private static navSevOfState(state: string): NavSev {
    if (state === 'ok') return 'ok';
    if (state === 'warning') return 'warning';
    if (state === 'offline') return 'offline';
    if (state === 'critical' || state === 'unreachable') return 'danger';
    return 'unknown';
  }

  /** Device tabs, with the open issues as badges. A tab that can't be
   *  opened keeps its reason, which the navigation shows as a title. */
  private navSections(): NavEntry[] {
    if (this.fleetMode) return [];

    // count the issues per tab, and remember the tabs with a critical one
    const countPerTab = new Map<string, number>();
    const hasCritical = new Map<string, boolean>();
    for (const issue of this.issues) {
      const tab = evidenceOf(issue.id ?? '')?.tab;
      if (!tab) continue;
      countPerTab.set(tab, (countPerTab.get(tab) ?? 0) + 1);
      if (issue.sev === 'danger') {
        hasCritical.set(tab, true);
      }
    }

    const entries: NavEntry[] = [];
    for (const tab of TABS) {
      // the overview shows every issue
      const isOverview = tab.id === 'overview';
      const count = isOverview ? this.issues.length : (countPerTab.get(tab.id) ?? 0);
      let badgeBad: boolean;
      if (isOverview) {
        badgeBad = this.issues.some((issue) => issue.sev === 'danger');
      } else {
        badgeBad = !!hasCritical.get(tab.id);
      }
      entries.push({
        id: tab.id,
        label: tab.label,
        icon: TAB_ICONS[tab.id],
        badge: count || null,
        badgeBad,
        off: this.tabOffReason(tab.id),
      });
    }
    return entries;
  }

  /** Sections of the all-devices page, without the ones it doesn't show. */
  private navFleetSections(): NavEntry[] {
    if (!this.fleetMode) return [];
    const fleet = this.fleet;

    const isShown = (id: string): boolean => {
      // before the fleet component exists, only the first two are sure
      if (!fleet) return id === 'top' || id === 'devices';
      if (id === 'events') return fleet.eventsSupported;
      if (id === 'sdwan') return fleet.showSdwan;
      if (id === 'hygiene') return fleet.hygieneSupported;
      return true;
    };
    const openIssues = fleet?.issues.length ?? 0;
    const licenseCount = fleet?.licenses.length ?? 0;

    const entries: NavEntry[] = [];
    for (const section of FLEET_SECTIONS) {
      if (!isShown(section.id)) continue;
      let badge: number | null = null;
      if (section.id === 'issues') {
        badge = openIssues || null;
      } else if (section.id === 'licenses') {
        badge = licenseCount || null;
      }
      const badgeBad = section.id === 'issues'
        && !!fleet?.issues.some((issue) => issue.sev === 'danger');
      entries.push({
        id: section.id,
        label: section.label,
        icon: section.icon,
        badge,
        badgeBad,
        off: '',
      });
    }
    return entries;
  }

  setPickerFilter(text: string): void {
    this.pickerFilter = text;
    this.buildPicker();
  }

  /** "Show" on an issue of the fleet page. */
  onFleetIssue(target: { serial: string; id: string }): void {
    this.pendingEvidence = target.id;
    if (!this.fleetMode && this.selectedSerial === target.serial) {
      this.goToEvidence(target.id);
      return;
    }
    this.setSerial(target.serial);
  }

  /** The fleet view keeps the header's device list and range buttons in
   *  sync, since no device document arrives while it's shown. */
  onFleetLoaded(doc: FleetDocument): void {
    if (!this.fleetMode) return;
    // the fleet page loads the new window by itself (its `range` input)
    this.finishPendingRange(true);
    this.serials = doc.meta.serials;
    this.devices = [];
    for (const device of doc.devices) {
      this.devices.push({ serial: device.serial, label: device.label, groups: groupsOfDevice(device) });
    }
    // the group asked by a click in the device header is applied: forget it
    this.pendingFleetGroup = null;
    this.buildPicker();
    if (!this.selectedSerial && doc.meta.serials.length) {
      this.selectedSerial = doc.meta.serials[0];
    }
    if (doc.meta.history_ranges?.length) {
      this.ranges = doc.meta.history_ranges;
    }
    if (!this.histRange) {
      this.histRange = doc.meta.history_range;
    }
    this.bucketed = !!doc.meta.bucketed;
    for (const device of doc.devices) {
      this.navStates.set(device.serial, FortinetDashboardComponent.navSevOfState(device.state));
    }
    this.publishNav();
    this.cdr.markForCheck();
  }

  onFleetStatus(status: { text: string; err: boolean }): void {
    if (!this.fleetMode) return;
    if (status.err) {
      this.finishPendingRange(false);
    }
    let text = status.text;
    if (this.paused && !status.err) {
      text += ' (auto-refresh paused)';
    }
    this.statusText = text;
    this.statusErr = status.err;
    this.cdr.markForCheck();
  }

  toggleInactiveLicenses(): void {
    this.showInactiveLicenses = !this.showInactiveLicenses;
  }

  toggleUsedPolicies(): void {
    this.showAllUsedPolicies = !this.showAllUsedPolicies;
    this.updatePolicyViews();
  }

  toggleLessUsedPolicies(): void {
    this.showAllLessUsedPolicies = !this.showAllLessUsedPolicies;
    this.updatePolicyViews();
  }

  setIfaceFilter(text: string): void {
    this.ifaceFilter = text;
    this.updateIfaceView();
  }

  /** A click on a column title, in any table of the page. The first click
   *  sorts on the column, the second flips the direction, the third goes
   *  back to the default order (see TableSort). */
  sortTable(sort: TableSort, key: string): void {
    sort.toggle(key);
    // Every table is sorted again: they are small, and this way I don't
    // need to know which table the sort belongs to.
    this.updateIfaceView();
    this.updatePolicyViews();
    this.updateSmallTables();
  }

  /** Width of the utilization bar, 0 to 100. */
  utilWidth(util: number | null | undefined): number {
    if (util == null) return 0;
    return Math.max(0, Math.min(100, util));
  }

  /** The interface table as shown (filter and order included). */
  exportIfacesCsv(): void {
    const rows: unknown[][] = [];
    for (const row of this.ifaceShown) {
      let duplex = '';
      if (row.duplex != null) {
        duplex = row.duplex === 0 ? 'half' : 'full';
      }
      rows.push([
        row.name, row.alias, row.ip, row.link ? 'up' : 'down', row.speed, duplex,
        row.rx_bps, row.tx_bps, row.util, row.rx_errors, row.tx_errors,
        row.rx_bytes, row.tx_bytes,
      ]);
    }
    downloadCsv(`fortigate-${this.serial}-interfaces`,
      ['interface', 'alias', 'ip', 'link', 'speed_mbps', 'duplex', 'rx_bps',
       'tx_bps', 'util_pct', 'rx_errors_lifetime', 'tx_errors_lifetime',
       'rx_bytes', 'tx_bytes'],
      rows);
  }

  /** Every policy of the device, busiest first. */
  exportPoliciesCsv(): void {
    const all = (this.lastDoc?.policy_totals ?? []).slice();
    // no current throughput sorts as -1, so after the real zeros
    all.sort((first, second) => (second.bps_now ?? -1) - (first.bps_now ?? -1));
    const unused = new Set(this.lastDoc?.policy_hygiene?.unused ?? []);

    const rows: unknown[][] = [];
    for (const policy of all) {
      const isUnused = unused.has(policy.name) || policy.hits === 0;
      rows.push([
        policy.name, policy.bps_now, policy.active_sessions, policy.hits, policy.bytes,
        isUnused ? 'yes' : 'no',
        this.oversizeNames.has(policy.name) ? 'yes' : 'no',
      ]);
    }
    downloadCsv(`fortigate-${this.serial}-policies`,
      ['policy', 'bps_now', 'active_sessions', 'hits', 'bytes_total',
       'unused', 'oversize'],
      rows);
  }

  setPolicyFilter(text: string): void {
    this.policyFilter = text;
    this.updatePolicyViews();
  }

  /** Header toggle on the destination ports card. */
  setDportsByVolume(byVolume: boolean): void {
    if (this.dportsByVolume === byVolume) return;
    this.dportsByVolume = byVolume;
    this.scheduleCharts();
  }

  /** Tab click, or arrow keys on the issues tab list. */
  selectIssuesTab(tab: 'open' | 'acked', element?: HTMLElement): void {
    this.issuesTab = tab;
    element?.focus();
    this.cdr.markForCheck();
  }

  /** Arrows, Home and End on the issues tabs (see issuesTabFromKey). */
  onIssuesTabKey(event: KeyboardEvent, tabOpen: HTMLElement, tabAcked: HTMLElement): void {
    const nextTab = issuesTabFromKey(event.key, this.issuesTab);
    if (nextTab === null) {
      return;
    }
    event.preventDefault();
    if (nextTab === 'open') {
      this.selectIssuesTab('open', tabOpen);
    } else {
      this.selectIssuesTab('acked', tabAcked);
    }
  }

  /** Checkbox on an issue: I move it between the open and acknowledged
   *  lists right away, save it, then refresh so the page matches what the
   *  backend stored (if the save fails, that puts the issue back). */
  setAck(issue: Issue, acked: boolean): void {
    const serial = this.selectedSerial;
    if (!issue.id || !serial || this.ackPending.has(issue.id)) return;
    const id = issue.id;

    const pending = new Set(this.ackPending);
    pending.add(id);
    this.ackPending = pending;

    if (acked) {
      this.issues = this.issues.filter((other) => other.id !== id);
      const nowAcked = { ...issue, acked_at: Date.now(), acked_sev: issue.sev, present: true };
      this.issuesAcked = sortAckedIssues([nowAcked, ...this.issuesAcked]);
    } else {
      this.issuesAcked = this.issuesAcked.filter((other) => other.id !== id);
      // only back in the open list if it's still happening
      if (issue.present !== false) {
        const reopened = { ...issue, acked_at: null, acked_sev: null };
        this.issues = sortOpenIssues([...this.issues, reopened]);
      }
    }
    this.cdr.markForCheck();

    this.svc.setIssueAck(serial, issue, acked, this.apiBase)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => this.ackDone(id),
        error: (error: unknown) => {
          this.ackDone(id);
          this.statusErr = true;
          this.statusText = 'Could not save the acknowledgement: '
            + describeApiError(error);
          this.cdr.markForCheck();
        },
      });
  }

  private ackDone(id: string): void {
    const next = new Set(this.ackPending);
    next.delete(id);
    this.ackPending = next;
    this.cdr.markForCheck();
    this.refresh();
  }

  // ------------------------------------------------------------- events
  setEventKind(kind: EventKind | 'all'): void {
    // clicking the active kind again goes back to all
    this.eventKind = this.eventKind === kind ? 'all' : kind;
    this.updateEventsView();
    this.scheduleCharts();
  }

  toggleAllEvents(): void {
    this.showAllEvents = !this.showAllEvents;
    this.updateEventsView();
  }

  private updateEventsView(): void {
    const kind = this.eventKind;
    let list = this.events;
    if (kind !== 'all') {
      list = this.events.filter((event) => event.kind === kind);
    }
    this.eventsMatching = list.length;
    this.eventsShown = this.showAllEvents ? list : list.slice(0, EVENT_ROWS);
    // 28 px per kind (at least 2 rows) plus room for the axis
    this.eventsChartHeight = 70 + 28 * Math.max(2, eventKindsOf(list).length);
  }

  toggleBuiltinCerts(): void {
    this.showBuiltinCerts = !this.showBuiltinCerts;
    this.updateSmallTables();
  }

  // ------------------------------------------------------------- ack notes
  /** "Note" on an acknowledged issue: opens its form. */
  openNote(issue: Issue): void {
    if (issue.id) {
      this.noteEd.open(issue.id, issue);
    }
    this.cdr.markForCheck();
  }

  cancelNote(): void {
    this.noteEd.close();
    this.cdr.markForCheck();
  }

  /** Saves the note: an acknowledgement that already exists only gets its
   *  note and name updated (dashboard.IssueAckStore). */
  saveNote(issue: Issue): void {
    const serial = this.selectedSerial;
    if (!issue.id || !serial || this.noteEd.saving) return;
    this.noteEd.saving = true;
    this.svc.setIssueAck(serial, issue, true, this.apiBase, this.noteEd.payload())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.noteEd.close();
          this.cdr.markForCheck();
          this.refresh();
        },
        error: (error: unknown) => {
          this.noteEd.saving = false;
          this.noteEd.error = 'Not saved: ' + describeApiError(error);
          this.cdr.markForCheck();
        },
      });
  }

  // ------------------------------------------------------------- evidence
  hasEvidence(issue: Issue): boolean {
    return evidenceOf(issue.id) != null;
  }

  eventHasEvidence(event: FortiEvent): boolean {
    return evidenceOf(event.ref) != null;
  }

  /** Opens the tab holding the data behind an issue and highlights it. */
  goToEvidence(id: string | undefined): void {
    const evidence = evidenceOf(id);
    if (evidence) {
      this.showEvidence(evidence);
    }
  }

  showEvidence(evidence: Evidence): void {
    // A row of the Endpoint polls card hidden by the card's filter can't be
    // highlighted: every row is shown first.
    if (this.pollFilter !== 'all') {
      for (const anchor of evidence.anchors) {
        if (anchor.startsWith('poll:')) {
          this.setPollFilter('all');
          break;
        }
      }
    }
    this.setTab(evidence.tab);
    this.cdr.markForCheck();
    // once the tab content exists (and its charts are drawn)
    this.zone.runOutsideAngular(() => setTimeout(() => {
      const host = this.hostRef.nativeElement;
      // the most precise anchor that exists on the page
      let target: HTMLElement | null = null;
      for (const anchor of evidence.anchors) {
        target = host.querySelector<HTMLElement>(`[data-ev="${CSS.escape(anchor)}"]`);
        if (target) break;
      }
      if (!target) return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
      target.classList.remove('flash');
      // reading offsetWidth forces a reflow, which restarts the animation
      void target.offsetWidth;
      target.classList.add('flash');
      const flashed = target;
      setTimeout(() => flashed.classList.remove('flash'), 2400);
    }, 60));
  }

  // ------------------------------------------------------------- trackBy
  trackByIndex = (index: number): number => index;
  trackByName = (_index: number, item: { name: string }): string => item.name;
  trackByRangeId = (_index: number, range: HistoryRange): string => range.id;
  trackBySerial = (_index: number, device: DeviceRef): string => device.serial;
  trackByTab = (_index: number, tab: { id: string }): string => tab.id;
  trackByKpi = (_index: number, kpi: KpiVm): string => kpi.label;
  trackByCheck = (_index: number, check: CheckVm): string => check.id;
  trackByIssue = (_index: number, issue: Issue): string => issue.id ?? issue.sev + '|' + issue.title;
  trackByHa = (_index: number, member: HaMember): string => member.serial || member.hostname;
  trackByEvent = (_index: number, event: FortiEvent): string =>
    `${event.t}|${event.kind}|${event.subject}|${event.title}`;
  trackByPeer = (_index: number, peer: { neighbor: string }): string => peer.neighbor;
  trackByRouting = (_index: number, row: RoutingRowVm): string => row.ev;
  trackByKind = (_index: number, kind: string): string => kind;
  trackByServer = (_index: number, server: NtpServer): string => server.server + '|' + server.ip;
  trackByPoll = (_index: number, item: PollRowVm): string => item.row.suffix;
  tabLabel = (id: DeviceTab | undefined): string =>
    TABS.find((tab) => tab.id === id)?.label ?? '';
  trackByAdmin = (_index: number, admin: AdminEntry): string =>
    admin.name + '@' + admin.ip + '#' + (admin.login_time ?? '');

  // -------------------------------------------------------- view helpers
  deviceText(device: DeviceRef): string {
    if (device.label && device.label !== device.serial) {
      return `${device.label} (${device.serial})`;
    }
    return device.serial;
  }

  private labelOf(serial: string): string {
    const device = this.devices.find((item) => item.serial === serial);
    return device?.label || serial;
  }

  /** Badge of a license row: lifecycle first (expired, expiring soon),
   *  then whether it's a real entitlement. */
  licenseBadge(license: LicenseEntry): Badge {
    const status = (license.status || '').toLowerCase();
    if (license.tier === 'expired' || status.includes('expired')) {
      return { cls: 'label-danger', text: 'expired' };
    }
    if (license.days_left != null) {
      if (license.days_left < 0) {
        return { cls: 'label-danger', text: 'expired' };
      }
      const verdict = daysLevelOf(license.days_left, this.thresholds.license_days);
      let cls: Badge['cls'] = 'label-success';
      if (verdict === 'danger') {
        cls = 'label-danger';
      } else if (verdict === 'warning') {
        cls = 'label-warning';
      }
      return { cls, text: `${license.days_left}d left` };
    }
    if (license.tier === 'inactive') {
      return { cls: 'label-info', text: license.status || 'none' };
    }
    return { cls: 'label-success', text: license.status || 'ok' };
  }

  sdwanBadge(member: SdwanMember): Badge {
    const isUp = (member.status || '').toLowerCase() === 'up';
    if (!isUp) {
      return { cls: 'label-danger', text: member.status || 'down' };
    }
    if (member.loss != null && member.loss > this.thresholds.sdwan_loss.warning) {
      return { cls: 'label-warning', text: `${member.loss.toFixed(1)}% loss` };
    }
    return { cls: 'label-success', text: 'up' };
  }

  sdwanCls(value: number | null, kind: 'latency' | 'jitter'): string {
    if (kind === 'latency') {
      return levelCls(value, this.thresholds.sdwan_latency);
    }
    return levelCls(value, this.thresholds.sdwan_jitter);
  }

  certBadge(cert: CertEntry): Badge {
    if (cert.days_left == null) {
      return { cls: 'label-info', text: cert.status || '?' };
    }
    if (cert.days_left < 0) {
      // a factory certificate is renewed by firmware, so it's only info
      return { cls: cert.built_in ? 'label-info' : 'label-danger', text: 'expired' };
    }
    const verdict = daysLevelOf(cert.days_left, this.thresholds.cert_days);
    let cls: Badge['cls'];
    if (cert.built_in && verdict) {
      cls = 'label-info';
    } else if (verdict === 'danger') {
      cls = 'label-danger';
    } else if (verdict === 'warning') {
      cls = 'label-warning';
    } else {
      cls = 'label-success';
    }
    return { cls, text: `${cert.days_left}d left` };
  }

  certTitle(cert: CertEntry): string {
    const parts = [
      cert.subject ? 'CN ' + cert.subject : '',
      cert.type,
      cert.source,
      cert.expires ? 'expires ' + fmtStamp(cert.expires * 1000) : '',
    ];
    return parts.filter(Boolean).join(', ');
  }

  /** "45 s ahead", "2.1 s behind", "in sync" */
  skewText(value: number | null | undefined): string {
    if (value == null) return '-';
    const absolute = Math.abs(value);
    if (absolute < 0.5) return 'in sync';
    const number = absolute < 10 ? absolute.toFixed(1) : absolute.toFixed(0);
    return `${number} s ${value > 0 ? 'ahead' : 'behind'}`;
  }

  skewCls(value: number | null | undefined): string {
    if (value == null) return 'dim';
    return levelCls(Math.abs(value), this.thresholds.clock_skew);
  }

  fmtMs(value: number | null | undefined): string {
    if (value == null) return '-';
    return value.toFixed(value < 10 ? 1 : 0) + ' ms';
  }

  utilClass(util: number | null | undefined): string {
    return levelCls(util, this.thresholds.util);
  }

  pctClass(value: number | null | undefined, kind: 'cpu' | 'memory'): string {
    if (!isPct(value)) return 'dim';
    return levelCls(value, this.thresholds[kind]);
  }

  /** "2 h ago", with the full date in the title. */
  loginAge(seconds: number | null | undefined): string {
    if (seconds == null) return '-';
    return fmtAge(Math.max(0, Date.now() / 1000 - seconds)) + ' ago';
  }

  loginTitle(seconds: number | null | undefined): string {
    if (seconds == null) return '';
    return 'Logged in ' + fmtStamp(seconds * 1000);
  }

  // ------------------------------------------------------------- loading
  private onDocument(data: DashboardDocument): void {
    if (this.destroyed || this.fleetMode) return;
    if (data.meta?.history_range) {
      this.histRange = data.meta.history_range;
    }
    // The backend echoes the serial it really served (it falls back to its
    // default for an unknown one), so I trust that echo.
    if (data.meta?.serials) {
      this.serials = data.meta.serials;
    } else if (data.meta?.serial) {
      this.serials = [data.meta.serial];
    } else {
      this.serials = [];
    }
    if (data.meta?.devices) {
      this.devices = data.meta.devices;
    } else {
      this.devices = this.serials.map((serial) => ({ serial, label: serial }));
    }
    this.selectedSerial = data.meta?.serial || null;
    this.lastDoc = data;
    this.receivedAt = Date.now();
    this.render(data);
    // switchMap cancels older requests, so this document is the one of
    // the window I clicked last
    this.finishPendingRange(true);
    this.writeUrl();

    const time = new Date().toLocaleTimeString();
    if (data.meta?.last_error) {
      this.statusErr = true;
      this.statusText = `Device poll issue: ${data.meta.last_error} (${time})`;
    } else {
      this.statusErr = false;
      this.statusText = 'Updated ' + time;
    }
    if (this.paused) {
      this.statusText += ' (auto-refresh paused)';
    }
    if (this.shareOpen) {
      this.buildShare();
    }
    this.publishNav();
    this.cdr.markForCheck();

    // an issue "Show" from the fleet page was waiting for this document
    if (this.pendingEvidence) {
      const id = this.pendingEvidence;
      this.pendingEvidence = null;
      this.goToEvidence(id);
    }
  }

  private onError(error: unknown): void {
    if (this.destroyed || this.fleetMode) return;
    this.finishPendingRange(false);
    this.statusErr = true;
    this.statusText = 'Refresh failed: ' + describeApiError(error);
    this.cdr.markForCheck();
  }

  private buildPicker(): void {
    const query = this.pickerFilter.trim().toLowerCase();
    const list = this.devices.filter((device) => {
      // the selected device always stays in the list
      if (!query || device.serial === this.selectedSerial) return true;
      // the search looks in every group of the device, not only its main one
      const groupsText = groupsOfDevice(device).join(' ');
      const text = `${device.label} ${device.serial} ${groupsText}`.toLowerCase();
      return text.includes(query);
    });
    this.pickerDevices = list;

    // Only group the picker when at least one device has a group.
    // Each device is listed once, under its MAIN group ('' when it has none).
    const devicesByMainGroup = new Map<string, DeviceRef[]>();
    const someDeviceHasAGroup = this.devices.some((device) => groupsOfDevice(device).length > 0);
    if (someDeviceHasAGroup) {
      for (const device of list) {
        const mainGroup = mainGroupOf(groupsOfDevice(device));
        const devicesOfGroup = devicesByMainGroup.get(mainGroup) ?? [];
        devicesOfGroup.push(device);
        devicesByMainGroup.set(mainGroup, devicesOfGroup);
      }
    }
    const groupNames = [...devicesByMainGroup.keys()];
    // each group before the groups inside it, "No group" last
    groupNames.sort(compareGroupsAsTree);
    this.pickerGroups = [];
    for (const groupName of groupNames) {
      const label = groupName === '' ? 'No group' : groupPathText(groupName);
      this.pickerGroups.push({ label, devices: devicesByMainGroup.get(groupName) ?? [] });
    }
  }

  get showPickerFilter(): boolean {
    return this.devices.length > PICKER_FILTER_MIN;
  }

  // ------------------------------------------------------------- render
  /** View models from a document. The charts follow once the template has
   *  caught up (scheduleCharts). */
  private render(doc: DashboardDocument): void {
    this.thresholds = thresholdsOf(doc.meta.thresholds);
    this.bucketed = !!doc.meta.bucketed;
    this.serial = doc.meta.serial || '-';
    this.version = doc.meta.version || '-';
    this.ranges = doc.meta.history_ranges || [];
    this.deviceInfo = doc.meta.device ?? null;

    this.headerGroups = [];
    if (this.deviceInfo) {
      const groups = groupsOfDevice(this.deviceInfo);
      for (let index = 0; index < groups.length; index++) {
        this.headerGroups.push({ name: groups[index], text: groupPathText(groups[index]), main: index === 0 });
      }
    }

    // title: the host name, otherwise a label that isn't just the serial,
    // otherwise the serial
    const info = this.deviceInfo;
    let labelIfUseful = '';
    if (info?.label && info.label !== this.serial) {
      labelIfUseful = info.label;
    }
    this.deviceTitle = info?.hostname || labelIfUseful || this.serial;
    this.updateStale();
    this.buildPicker();

    // ---- policies ----
    const allPolicies = doc.policy_totals || [];
    this.policyUsedRows = allPolicies.filter((policy) => policy.hits > 0);
    this.policyUsedRows.sort((first, second) => (second.bps_now ?? -1) - (first.bps_now ?? -1));
    const unused = allPolicies.filter((policy) => policy.hits === 0);
    unused.sort((first, second) => first.name.localeCompare(second.name));
    this.lessUsedShowsUnused = unused.length > 0;
    if (this.lessUsedShowsUnused) {
      this.policyLessUsedRows = unused;
    } else {
      // No unused policies: I show the ones with the fewest hits instead.
      const byHits = allPolicies.slice();
      byHits.sort((first, second) =>
        first.hits - second.hits || first.name.localeCompare(second.name));
      this.policyLessUsedRows = byHits;
    }
    this.oversizeNames = new Set(doc.policy_hygiene?.oversize ?? []);
    this.updatePolicyViews();

    // ---- issues ----
    this.issues = doc.issues || [];
    this.ackSupported = Array.isArray(doc.issues_acked);
    this.issuesAcked = doc.issues_acked || [];
    this.issuesLoaded = true;

    // ---- interfaces ----
    // every interface, busiest first (dashboard.py sends them in that order)
    this.ifaceRows = doc.iface_latest || [];
    this.reportIfaceRows = this.ifaceRows.slice(0, REPORT_IFACE_ROWS);
    this.reportIfaceTitle = 'Interfaces';
    if (this.ifaceRows.length > this.reportIfaceRows.length) {
      this.reportIfaceTitle += ` (${this.reportIfaceRows.length} busiest of ${this.ifaceRows.length})`;
    }
    let hasHalfDuplex = false;
    for (const row of this.ifaceRows) {
      // a port without link often says "half": only a link that is up counts
      if (row.link && row.duplex === 0) {
        hasHalfDuplex = true;
      }
    }
    // The table card gets wider (and the throughput chart narrower) when
    // the Duplex column appears or goes away: the chart must be resized
    // after that, or it keeps its old width.
    if (hasHalfDuplex !== this.ifaceHasHalfDuplex) {
      this.resizeAfterCharts = true;
    }
    this.ifaceHasHalfDuplex = hasHalfDuplex;
    this.ifaceErr = this.buildIfaceErr(this.ifaceRows);
    this.updateIfaceView();

    // ---- device health / VPN / WAN (all optional on older backends) ----
    this.hasHealth = doc.firmware !== undefined || doc.license !== undefined
      || doc.sdwan !== undefined || doc.vpn_tunnels !== undefined;
    this.firmware = doc.firmware || null;
    const licenses = doc.license || [];
    this.licenseActive = licenses.filter((license) => license.tier !== 'inactive');
    this.licenseInactive = licenses.filter((license) => license.tier === 'inactive');
    this.sensorRows = doc.sensors || [];
    this.vpnTunnels = doc.vpn_tunnels || [];
    this.sdwanRows = (doc.sdwan && doc.sdwan.latest) || [];
    this.haMembers = (doc.ha && doc.ha.members) || [];
    this.fortiguard = doc.fortiguard || null;
    this.adminRows = doc.admins || [];

    // ---- time, certificates, routing, events ----
    this.systemState = doc.system_state ?? null;
    this.ntp = doc.ntp ?? null;
    this.certRows = doc.certificates ?? [];
    this.bgpRows = doc.routing?.bgp ?? [];
    this.ospfRows = doc.routing?.ospf ?? [];
    this.routingRows = this.buildRoutingRows();
    this.hasRouting = this.bgpRows.length > 0 || this.ospfRows.length > 0;
    this.pollReport = doc.poll_report ?? null;
    this.pollRows = this.buildPollRows();
    this.updateSmallTables();
    this.events = doc.events ?? [];
    this.eventsTotal = doc.events_total ?? this.events.length;
    this.eventKinds = eventKindsOf(this.events);
    if (this.eventKind !== 'all' && !this.eventKinds.includes(this.eventKind)) {
      this.eventKind = 'all';
    }
    this.updateEventsView();
    // a note form whose issue isn't acknowledged anymore gets closed
    if (this.noteEd.key && !this.issuesAcked.some((issue) => issue.id === this.noteEd.key)) {
      this.noteEd.close();
    }

    // ---- which cards have something to show ----
    const sdwan = doc.sdwan;
    const ssl = doc.ssl_users_ts;
    this.hasSdwan = !!(sdwan && sdwan.t.length && sdwan.series.length);
    this.hasTunnels = this.vpnTunnels.length > 0;
    let sslHasValues = false;
    if (ssl && ssl.t.length) {
      sslHasValues = ssl.users.some((value) => value != null)
        || ssl.tunnels.some((value) => value != null);
    }
    this.hasSslUsers = sslHasValues;
    this.hasIfaceErrors = !!(doc.iface_errors && doc.iface_errors.t.length
      && doc.iface_errors.series.length);
    this.hasWanVpn = this.hasSdwan || this.sdwanRows.length > 0
      || this.hasTunnels || this.hasSslUsers || this.hasRouting;
    this.ports = doc.ports ?? null;
    this.netmap = doc.session_traffic?.bucket?.netmap ?? null;
    this.buildCoverage(doc.session_traffic?.bucket);
    this.takeNetmapState(doc);
    this.hasPorts = !!this.ports?.available;
    // A URL, a device switch or a narrower window can land on a tab that is
    // now empty: back to the overview (the URL follows in onDocument)
    if (this.tabOffReason(this.tab)) {
      this.tab = 'overview';
    }

    this.kpis = this.buildKpis(doc);
    this.buildChecks(doc);
    this.scheduleCharts();
  }

  private updateStale(): void {
    const info = this.deviceInfo;
    if (!info || !info.stale) {
      this.staleText = '';
      return;
    }
    if (info.poll_age == null) {
      this.staleText = 'No poll recorded';
      return;
    }
    // the age the backend gave, plus the time since I received it
    const age = info.poll_age + (Date.now() - this.receivedAt) / 1000;
    this.staleText = `Last poll ${fmtAge(age)} ago`;
  }

  private updateIfaceView(): void {
    const query = this.ifaceFilter.trim().toLowerCase();
    let shown = this.ifaceRows;
    if (query) {
      shown = this.ifaceRows.filter((row) => {
        const text = `${row.name} ${row.alias ?? ''} ${row.ip ?? ''}`.toLowerCase();
        return text.includes(query);
      });
    }

    this.ifaceShown = this.ifaceSort.apply(shown, ifaceSortValue);
  }

  /** The BGP and OSPF peers as rows of the routing table. */
  private buildRoutingRows(): RoutingRowVm[] {
    const rows: RoutingRowVm[] = [];

    for (const peer of this.bgpRows) {
      let cls = 'label-info';
      if (peer.up) {
        cls = 'label-success';
      } else if (peer.admin_up) {
        cls = 'label-danger';
      }
      let details = '';
      if (peer.remote_as != null) {
        details += 'AS ' + peer.remote_as;
      }
      if (peer.local_ip) {
        details += ', local ' + peer.local_ip;
      }
      rows.push({
        protocol: 'BGP',
        neighbor: peer.neighbor,
        state: peer.admin_up ? peer.state : 'admin down',
        cls,
        details,
        ev: 'bgp:' + peer.neighbor,
      });
    }

    for (const peer of this.ospfRows) {
      let details = '';
      if (peer.router_id) {
        details += 'router id ' + peer.router_id;
      }
      if (peer.priority != null) {
        details += ', priority ' + peer.priority;
      }
      rows.push({
        protocol: 'OSPF',
        neighbor: peer.neighbor,
        state: peer.state,
        cls: peer.up ? 'label-success' : 'label-danger',
        details,
        ev: 'ospf:' + peer.neighbor,
      });
    }
    return rows;
  }

  /** Sorts the small tables of the VPN and System tabs (and the licenses
   *  of the overview), each with its own TableSort. The lists they come
   *  from stay in the order of the document. */
  private updateSmallTables(): void {
    this.licenseActiveShown = this.licenseSort.apply(this.licenseActive, licenseSortValue);
    this.licenseInactiveShown = this.licenseSort.apply(this.licenseInactive, licenseSortValue);
    this.sensorShown = this.sensorSort.apply(this.sensorRows, sensorSortValue);
    this.tunnelsShown = this.tunnelSort.apply(this.vpnTunnels, tunnelSortValue);
    this.sdwanShown = this.sdwanSort.apply(this.sdwanRows, sdwanSortValue);
    this.routingShown = this.routingSort.apply(this.routingRows, routingSortValue);
    this.haShown = this.haSort.apply(this.haMembers, haSortValue);
    this.adminShown = this.adminSort.apply(this.adminRows, adminSortValue);
    this.ntpShown = this.ntpSort.apply(this.ntp?.servers ?? [], ntpSortValue);

    // the factory certificates are folded away unless I ask for them
    const certs: CertEntry[] = [];
    for (const cert of this.certRows) {
      if (this.showBuiltinCerts || !cert.built_in) {
        certs.push(cert);
      }
    }
    this.certsShown = this.certSort.apply(certs, certSortValue);

    // Endpoint polls: only the rows of the chosen filter, then sorted
    this.pollShown = this.pollSort.apply(this.pollRowsOf(this.pollFilter), pollSortValue);
  }

  /** The rows of the Endpoint polls card that a filter button shows. */
  private pollRowsOf(filter: PollFilter): PollRowVm[] {
    const option = POLL_FILTERS.find((candidate) => candidate.id === filter);
    if (!option || option.verdicts.length === 0) {
      return this.pollRows;
    }
    const rows: PollRowVm[] = [];
    for (const item of this.pollRows) {
      if (option.verdicts.includes(item.row.verdict)) {
        rows.push(item);
      }
    }
    return rows;
  }

  /** The rows of the Endpoint polls card, with their pill and the little
   *  squares of their history already computed, so the template doesn't
   *  call a function for each of the 24 x 30 squares at every refresh. */
  private buildPollRows(): PollRowVm[] {
    const report = this.pollReport;
    const result: PollRowVm[] = [];
    if (!report?.available) {
      this.pollSummary = '';
      this.pollFilterCounts = { all: 0, look: 0, fine: 0, ok: 0 };
      return result;
    }

    for (const row of report.endpoints) {
      const marks: PollMarkVm[] = [];
      for (let cycleIndex = 0; cycleIndex < row.history.length; cycleIndex++) {
        const mark = row.history[cycleIndex];
        const time = report.times[cycleIndex] ?? null;
        marks.push({
          cls: this.pollMarkCls(row, mark),
          title: `${fmtStamp(time)}: ${this.pollMarkText(report, mark)}`,
        });
      }

      let okText = 'not polled';
      if (row.polled) {
        okText = `${row.ok} of ${row.polled} OK`;
      }

      result.push({ row, badge: POLL_BADGE[row.verdict], marks, okText });
    }

    // the numbers on the filter buttons
    const counts: Record<PollFilter, number> = { all: 0, look: 0, fine: 0, ok: 0 };
    for (const option of POLL_FILTERS) {
      let count = 0;
      for (const item of result) {
        if (option.verdicts.length === 0 || option.verdicts.includes(item.row.verdict)) {
          count += 1;
        }
      }
      counts[option.id] = count;
    }
    this.pollFilterCounts = counts;

    // the header line, without the parts that are zero
    const parts: string[] = [];
    if (counts.ok) parts.push(`${counts.ok} OK`);
    if (counts.fine) parts.push(`${counts.fine} not a problem`);
    if (counts.look) parts.push(`${counts.look} to look at`);
    this.pollSummary = parts.join(', ');
    return result;
  }

  /** The class of one little square: green 2xx, grey expected answer,
   *  red error, orange no answer, empty frame when not polled. */
  private pollMarkCls(row: PollEndpointRow, mark: PollMark): string {
    if (mark === 'skip') return 'pm-skip';
    if (mark === null) return 'pm-none';
    if (mark >= 200 && mark < 300) return 'pm-ok';
    if (row.expected.includes(mark)) return 'pm-expected';
    return 'pm-fail';
  }

  /** "404 Not Found", "no answer", "not polled" */
  private pollMarkText(report: PollReport, mark: PollMark): string {
    if (mark === 'skip') return 'not polled';
    if (mark === null) return 'no answer';
    const name = report.status_names[String(mark)];
    if (name) {
      return `${mark} ${name}`;
    }
    return `HTTP ${mark}`;
  }

  /** A button above the Endpoint polls table. */
  setPollFilter(filter: PollFilter): void {
    this.pollFilter = filter;
    this.updateSmallTables();
  }

  /** The Errors cell shows the lifetime counter, but its colour follows the
   *  issues raised on the window (dashboard.py judges the errors of the
   *  window against the packets moved). That way one CRC error months ago
   *  doesn't paint the cell red forever. */
  private buildIfaceErr(rows: IfaceLatest[]): Record<string, IfaceErrVm | undefined> {
    const result: Record<string, IfaceErrVm | undefined> = {};
    const prefixLength = 'iface-errors:'.length;

    // the worst open error issue of each interface
    const issueByIface = new Map<string, Issue>();
    for (const issue of this.issues) {
      if (!issueMatches(issue.id, ['iface-errors'])) continue;
      const name = issue.id!.slice(prefixLength);
      const previous = issueByIface.get(name);
      if (!previous || SEV_RANK[issue.sev] < SEV_RANK[previous.sev]) {
        issueByIface.set(name, issue);
      }
    }

    // the interfaces whose error issue is acknowledged (and still there)
    const acked = new Set<string>();
    for (const issue of this.issuesAcked) {
      if (issue.present !== false && issueMatches(issue.id, ['iface-errors'])) {
        acked.add(issue.id!.slice(prefixLength));
      }
    }

    for (const row of rows) {
      const total = row.rx_errors + row.tx_errors;
      const issue = issueByIface.get(row.name);
      if (issue) {
        let cls = '';
        if (issue.sev === 'danger') {
          cls = 'err-warn';
        } else if (issue.sev === 'warning') {
          cls = 'util-warn';
        }
        let title = `Lifetime counter. ${issue.title}`;
        if (issue.detail) {
          title += `: ${issue.detail}`;
        }
        result[row.name] = { cls, title };
      } else {
        let title = 'Lifetime counter';
        if (total) {
          if (acked.has(row.name)) {
            title += '; the errors in this window are acknowledged';
          } else {
            title += '; no errors worth reporting in this window';
          }
        }
        result[row.name] = { cls: 'dim', title };
      }
    }
    return result;
  }

  /** I compute the slices here instead of in getters, so change detection
   *  keeps seeing the same arrays between refreshes. */
  private updatePolicyViews(): void {
    const query = this.policyFilter.trim().toLowerCase();
    const matches = (policy: PolicyTotal) => !query || policy.name.toLowerCase().includes(query);
    // sorted before being cut, so "show the top 10" follows the column
    const used = this.policyUsedSort.apply(this.policyUsedRows.filter(matches), policySortValue);
    const lessUsed = this.policyLessUsedSort.apply(this.policyLessUsedRows.filter(matches),
                                                   policySortValue);
    this.policyUsedMatches = used.length;
    this.policyLessUsedMatches = lessUsed.length;
    const collapsedCount = this.policyRowsCollapsed;
    // a search shows every match
    if (this.showAllUsedPolicies || query) {
      this.policyUsedShown = used;
    } else {
      this.policyUsedShown = used.slice(0, collapsedCount);
    }
    if (this.showAllLessUsedPolicies || query) {
      this.policyLessUsedShown = lessUsed;
    } else {
      this.policyLessUsedShown = lessUsed.slice(0, collapsedCount);
    }
  }

  /** The map state from a new document. On the same device I keep my own
   *  copy when it's newer: a document fetched just before a save would
   *  otherwise bring the old state back (for instance when I come back to
   *  the map tab). */
  private takeNetmapState(doc: DashboardDocument): void {
    const incoming = doc.netmap_state ?? null;
    const serial = doc.meta.serial || null;
    const current = this.netmapState;
    let takeIt = true;
    if (serial === this.netmapStateSerial && current) {
      const incomingTime = incoming?.updated_at ?? 0;
      takeIt = incomingTime >= (current.updated_at ?? 0);
    }
    if (takeIt) {
      this.netmapState = incoming;
    }
    this.netmapStateSerial = serial;
  }

  /** The map saved its state: I keep the server's copy for next time. */
  onNetmapStateSaved(state: NetmapState): void {
    this.netmapState = state;
  }

  // ------------------------------------------------------------- KPIs
  /** A CPU or memory card, from the 'current' series of a vdom resource. */
  private gaugeKpi(label: string, timeSeries: TimeSeries | undefined,
                   kind: 'cpu' | 'memory', words: [string, string]): KpiVm {
    const series = pickSeries(timeSeries);
    const value = series ? valueFromEnd(series.values, 1) : null;
    if (!series || value == null) {
      return { label, tab: 'system', value: '-' };
    }
    const kpi: KpiVm = {
      label,
      tab: 'system',
      value: isPct(value) ? value.toFixed(0) + ' %' : fmtNum(value),
      trend: trendOf(value, valueFromEnd(series.values, 2), fmtNum, true),
    };
    if (isPct(value)) {
      const verdict = levelOf(value, this.thresholds[kind]);
      if (verdict === 'danger') {
        kpi.badge = { cls: 'label-danger', text: words[1] };
      } else if (verdict === 'warning') {
        kpi.badge = { cls: 'label-warning', text: words[0] };
      } else {
        kpi.badge = { cls: 'label-success', text: 'OK' };
      }
    } else {
      // not a percentage: I say which series it is instead of a verdict
      kpi.sub = series.name;
    }
    return kpi;
  }

  private buildKpis(doc: DashboardDocument): KpiVm[] {
    const summary = doc.iface_summary;
    const sessionsTs = doc.sessions_ts;

    // ---- throughput ----
    const throughputKnown = summary.total_rx_bps != null || summary.total_tx_bps != null;
    const throughput = (summary.total_rx_bps ?? 0) + (summary.total_tx_bps ?? 0);
    const previousThroughput = (summary.prev_rx_bps ?? 0) + (summary.prev_tx_bps ?? 0);
    let throughputKpi: KpiVm;
    if (throughputKnown) {
      throughputKpi = {
        label: 'Throughput', tab: 'traffic',
        value: fmtBps(throughput),
        trend: trendOf(throughput, previousThroughput || null, fmtBps),
        sub: `RX ${fmtBps(summary.total_rx_bps)}, TX ${fmtBps(summary.total_tx_bps)}`,
      };
    } else {
      throughputKpi = { label: 'Throughput', tab: 'traffic', value: '-', sub: 'waiting for two polls' };
    }

    // ---- sessions ----
    const lastCount = valueFromEnd(sessionsTs.session_count, 1);
    const matched = doc.session_traffic.latest_count;
    const sessionsKpi: KpiVm = {
      label: 'Active sessions', tab: 'traffic',
      value: lastCount == null ? '-' : fmtNum(lastCount),
      trend: lastCount == null ? null
        : trendOf(lastCount, valueFromEnd(sessionsTs.session_count, 2), fmtNum),
      sub: matched ? `${fmtNum(matched)} in latest detail sample` : '',
    };

    const setupRate = valueFromEnd(sessionsTs.setup_rate, 1);
    const rateKpi: KpiVm = {
      label: 'Session setup rate', tab: 'traffic',
      value: setupRate == null ? '-' : fmtNum(setupRate),
      suffix: setupRate == null ? undefined : ' /s',
      trend: setupRate == null ? null
        : trendOf(setupRate, valueFromEnd(sessionsTs.setup_rate, 2), fmtNum),
      sub: 'new sessions per second',
    };

    // ---- interfaces ----
    let interfacesKpi: KpiVm;
    if (summary.total > 0) {
      let badge: Badge;
      if (summary.down === 0) {
        badge = { cls: 'label-success', text: 'all up' };
      } else {
        badge = { cls: 'label-danger', text: `${summary.down} down` };
      }
      interfacesKpi = {
        label: 'Interfaces', tab: 'traffic',
        value: String(summary.up),
        suffix: `/ ${summary.total} up`,
        badge,
      };
    } else {
      interfacesKpi = { label: 'Interfaces', tab: 'traffic', value: '-' };
    }

    // ---- busiest policy ----
    // the first one with the highest current throughput wins
    let busiest: PolicyTotal | null = null;
    for (const policy of doc.policy_totals || []) {
      if ((policy.bps_now ?? -1) > (busiest?.bps_now ?? -1)) {
        busiest = policy;
      }
    }
    let policyKpi: KpiVm;
    if (busiest && busiest.bps_now != null) {
      policyKpi = { label: 'Busiest policy', tab: 'policies',
                    value: fmtBps(busiest.bps_now), sub: busiest.name };
    } else {
      policyKpi = { label: 'Busiest policy', tab: 'policies', value: '-' };
    }

    // ---- CPU and memory ----
    const cpuKpi = this.gaugeKpi('CPU', doc.cpu_ts, 'cpu', ['elevated', 'high']);
    const memoryKpi = this.gaugeKpi('Memory', doc.memory_ts, 'memory',
                                    ['elevated', 'conserve-mode risk']);

    const kpis: KpiVm[] = [throughputKpi, sessionsKpi, rateKpi, interfacesKpi,
                           policyKpi, cpuKpi, memoryKpi];

    // ---- SSL-VPN, only when there's data ----
    const ssl = doc.ssl_users_ts;
    const sslNow = ssl ? valueFromEnd(ssl.users, 1) : null;
    if (ssl && sslNow != null) {
      kpis.push({
        label: 'SSL-VPN users', tab: 'vpn',
        value: fmtNum(sslNow),
        trend: trendOf(sslNow, valueFromEnd(ssl.users, 2), fmtNum),
        sub: 'remote users connected',
      });
    }
    return kpis;
  }

  // ------------------------------------------------------------- checks
  /** The health-check grid: every check the data allows, pass or not.
   *  The verdict comes from the issues the backend raised for that check:
   *  fail (critical), warn, note, ack (acknowledged), otherwise pass. */
  private buildChecks(doc: DashboardDocument): void {
    const info = this.deviceInfo;
    const latest = doc.iface_latest || [];
    const cpu = pickSeries(doc.cpu_ts);
    const memory = pickSeries(doc.memory_ts);
    const policies = doc.policy_totals || [];
    const hygiene = doc.policy_hygiene;

    const utils: number[] = [];
    // only the links that are up: a port without link often reports "half"
    let halfDuplexCount = 0;
    for (const row of latest) {
      if (row.util != null) {
        utils.push(row.util);
      }
      if (row.link && row.duplex === 0) {
        halfDuplexCount++;
      }
    }

    // the license that expires first (the first one wins a tie)
    let soonestLicense: LicenseEntry | null = null;
    for (const license of this.licenseActive) {
      if (license.days_left == null) continue;
      if (soonestLicense == null || license.days_left < soonestLicense.days_left!) {
        soonestLicense = license;
      }
    }

    // how many issues (open or acknowledged) match some keys
    const allIssues = [...this.issues, ...this.issuesAcked];
    const countIssues = (keys: string[]): number =>
      allIssues.filter((issue) => issueMatches(issue.id, keys)).length;

    const sdwanUp = this.sdwanRows.filter((member) =>
      (member.status || '').toLowerCase() === 'up').length;
    let unusedCount: number;
    if (hygiene) {
      unusedCount = hygiene.unused.length;
    } else {
      unusedCount = policies.filter((policy) => policy.hits === 0).length;
    }

    // ---- the metric text of the checks that need a bit of work ----
    const firmwareTarget = info?.firmware_target;
    let firmwareMetric = this.version;
    if (firmwareTarget) {
      firmwareMetric += `, target ${firmwareTarget}`;
    }

    let licensesMetric = `${this.licenseActive.length} active`;
    if (soonestLicense) {
      licensesMetric = `soonest in ${soonestLicense.days_left} d`;
    }

    const sensorsInAlarm = this.sensorRows.filter((sensor) => sensor.alarm).length;

    let fortiguardMetric = '';
    if (this.fortiguard) {
      fortiguardMetric = `${this.fortiguard.connected} of ${this.fortiguard.servers} reachable`;
    }

    let conserveMetric = 'not in conserve mode';
    if (this.systemState?.conserve) {
      conserveMetric = 'in conserve mode now';
    } else if (this.systemState?.conserve_seen) {
      conserveMetric = 'recovered in the window';
    }

    const clockParts: string[] = [];
    if (this.systemState?.clock_skew != null) {
      clockParts.push(this.skewText(this.systemState.clock_skew));
    }
    if (this.ntp?.available) {
      clockParts.push(`${this.ntp.reachable} of ${this.ntp.servers.length} NTP reachable`);
    }

    // dashboard.py sorts the certificates by expiry, so the first of mine
    // is the one that expires soonest
    const ownCerts = this.certRows.filter((cert) => !cert.built_in && cert.days_left != null);
    const firstCert = ownCerts[0];
    let certsMetric: string;
    if (!firstCert) {
      certsMetric = `${this.certRows.length} factory only`;
    } else if (firstCert.days_left! < 0) {
      certsMetric = `${firstCert.name} expired`;
    } else {
      certsMetric = `soonest in ${firstCert.days_left} d`;
    }

    const haCount = this.haMembers.length;
    const errorIssues = countIssues(['iface-errors']);
    const pollIssues = countIssues(['poll-failing', 'poll-absent']);

    let freshnessMetric = 'no poll recorded';
    if (info?.poll_age != null) {
      freshnessMetric = `last poll ${fmtAge(info.poll_age)} ago`;
    }

    type CheckDef = {
      id: string; group: string; label: string; keys: string[];
      show: boolean; metric: string; ev: Evidence | null;
      // a verdict the data forces, whatever the issues say
      force?: { status: CheckStatus; title: string };
    };
    const evidence = (tab: DeviceTab, ...anchors: string[]): Evidence => ({ tab, anchors });

    const defs: CheckDef[] = [
      { id: 'cpu', group: 'System', label: 'CPU', keys: ['cpu'],
        show: !!cpu, metric: fmtPctOrNum(lastValue(cpu?.values)), ev: evidence('system', 'card:cpumem') },
      { id: 'memory', group: 'System', label: 'Memory', keys: ['memory'],
        show: !!memory, metric: fmtPctOrNum(lastValue(memory?.values)), ev: evidence('system', 'card:cpumem') },
      { id: 'firmware', group: 'System', label: 'Firmware', keys: ['firmware-upgrade'],
        show: this.version !== '-', ev: evidence('overview', 'card:licenses'),
        metric: firmwareMetric,
        force: info?.firmware_status === 'behind'
          ? { status: 'warn', title: `Behind the target version ${firmwareTarget}` } : undefined },
      { id: 'licenses', group: 'System', label: 'Licenses',
        keys: ['license-expired', 'license-expiring'],
        show: this.licenseActive.length > 0, ev: evidence('overview', 'card:licenses'),
        metric: licensesMetric },
      { id: 'sensors', group: 'System', label: 'Hardware sensors', keys: ['sensor'],
        show: this.sensorRows.length > 0, ev: evidence('system', 'card:sensors'),
        metric: `${sensorsInAlarm} of ${this.sensorRows.length} in alarm` },
      { id: 'fortiguard', group: 'System', label: 'FortiGuard updates', keys: ['fortiguard'],
        show: !!this.fortiguard?.available, ev: evidence('system', 'card:sensors'),
        metric: fortiguardMetric },
      { id: 'conserve', group: 'System', label: 'Conserve mode',
        keys: ['conserve', 'conserve-recent'],
        show: !!this.systemState?.available && this.systemState.conserve != null,
        ev: evidence('system', 'card:cpumem'),
        metric: conserveMetric },
      { id: 'clock', group: 'System', label: 'Clock & NTP', keys: ['clock-skew', 'ntp'],
        show: this.systemState?.clock_skew != null || !!this.ntp?.available,
        ev: evidence('system', 'card:time'),
        metric: clockParts.join(', ') },
      { id: 'certs', group: 'System', label: 'Certificates',
        keys: ['cert-expired', 'cert-expiring'],
        show: this.certRows.length > 0, ev: evidence('system', 'card:certs'),
        metric: certsMetric },
      { id: 'ha', group: 'System', label: 'HA cluster', keys: ['ha-single'],
        show: haCount > 0, ev: evidence('system', 'card:ha'),
        metric: `${haCount} member${haCount === 1 ? '' : 's'}` },

      { id: 'links', group: 'Network', label: 'Interface links', keys: ['iface-down', 'iface-flap'],
        show: latest.length > 0, ev: evidence('traffic', 'card:interfaces'),
        metric: `${doc.iface_summary.up} of ${doc.iface_summary.total} up` },
      { id: 'util', group: 'Network', label: 'Link utilization', keys: ['iface-util'],
        show: utils.length > 0, ev: evidence('traffic', 'card:interfaces'),
        metric: utils.length ? `busiest at ${Math.max(...utils).toFixed(0)} %` : '' },
      { id: 'errors', group: 'Network', label: 'Interface errors', keys: ['iface-errors'],
        show: latest.length > 0, ev: evidence('traffic', 'card:interfaces'),
        metric: errorIssues ? `${errorIssues} interface(s)` : 'none in the window' },
      { id: 'duplex', group: 'Network', label: 'Duplex', keys: ['iface-duplex'],
        show: latest.some((row) => row.duplex != null), ev: evidence('traffic', 'card:interfaces'),
        metric: `${halfDuplexCount} half-duplex` },
      { id: 'reset', group: 'Network', label: 'Counter continuity', keys: ['counter-reset'],
        show: latest.length > 0, ev: evidence('traffic', 'card:iface-chart'),
        metric: countIssues(['counter-reset']) ? 'reset in the window' : 'no reset' },

      { id: 'bgp', group: 'Network', label: 'BGP neighbors', keys: ['bgp-down', 'bgp-flap'],
        show: this.bgpRows.length > 0, ev: evidence('vpn', 'card:routing'),
        metric: `${this.bgpRows.filter((peer) => peer.up).length} of ${this.bgpRows.length} established` },
      { id: 'ospf', group: 'Network', label: 'OSPF neighbors', keys: ['ospf-down', 'ospf-flap'],
        show: this.ospfRows.length > 0, ev: evidence('vpn', 'card:routing'),
        metric: `${this.ospfRows.filter((peer) => peer.up).length} of ${this.ospfRows.length} adjacent` },

      { id: 'ipsec', group: 'VPN & WAN', label: 'IPsec tunnels', keys: ['ipsec-down', 'ipsec-flap'],
        show: this.hasTunnels, ev: evidence('vpn', 'card:tunnels'),
        metric: `${this.vpnTunnels.filter((tunnel) => tunnel.up).length} of ${this.vpnTunnels.length} up` },
      { id: 'sdwan', group: 'VPN & WAN', label: 'SD-WAN members', keys: ['sdwan-down', 'sdwan-loss', 'sdwan-flap'],
        show: this.sdwanRows.length > 0, ev: evidence('vpn', 'card:sdwan-members'),
        metric: `${sdwanUp} of ${this.sdwanRows.length} up` },
      { id: 'ssl', group: 'VPN & WAN', label: 'SSL-VPN', keys: [],
        show: this.hasSslUsers, ev: evidence('vpn', 'card:ssl'),
        metric: `${fmtNum(lastValue(doc.ssl_users_ts?.users))} users now` },

      { id: 'unused', group: 'Policies', label: 'Unused policies', keys: ['policy-unused'],
        show: policies.length > 0, ev: evidence('policies', 'card:less-used'),
        metric: `${unusedCount} of ${policies.length}` },
      { id: 'oversize', group: 'Policies', label: 'Oversize policies', keys: ['policy-oversize'],
        show: policies.length > 0, ev: evidence('policies', 'card:top-used'),
        metric: `${hygiene ? hygiene.oversize.length : countIssues(['policy-oversize'])}` },

      { id: 'collector-polls', group: 'Collector', label: 'Endpoint polls',
        keys: ['poll-failing', 'poll-absent'], show: true,
        ev: this.pollReport?.available ? evidence('system', 'card:polls') : null,
        metric: pollIssues ? `${pollIssues} endpoint(s) failing` : 'all answering' },
      { id: 'freshness', group: 'Collector', label: 'Data freshness', keys: [],
        show: !!info, ev: null,
        metric: freshnessMetric,
        force: info?.stale
          ? { status: 'fail', title: 'The collector has not polled this device recently' } : undefined },
    ];

    const groups = new Map<string, CheckVm[]>();
    const tally: Record<CheckStatus, number> = { pass: 0, note: 0, ack: 0, warn: 0, fail: 0 };
    for (const def of defs) {
      if (!def.show) continue;
      const open = this.issues.filter((issue) => issueMatches(issue.id, def.keys));
      const acked = this.issuesAcked.filter((issue) =>
        issue.present !== false && issueMatches(issue.id, def.keys));

      let status: CheckStatus;
      if (open.some((issue) => issue.sev === 'danger')) {
        status = 'fail';
      } else if (open.some((issue) => issue.sev === 'warning')) {
        status = 'warn';
      } else if (open.length) {
        status = 'note';
      } else if (acked.length) {
        status = 'ack';
      } else {
        status = 'pass';
      }

      const titles: string[] = [];
      for (const issue of [...open, ...acked]) {
        titles.push(issue.title + (acked.includes(issue) ? ' (acknowledged)' : ''));
      }
      // a forced verdict only wins when it's worse
      if (def.force && CHECK_RANK[def.force.status] < CHECK_RANK[status]) {
        status = def.force.status;
        titles.unshift(def.force.title);
      }
      tally[status]++;

      const check: CheckVm = {
        id: def.id, label: def.label, metric: def.metric, status,
        badge: CHECK_BADGE[status],
        title: titles.join('\n') || 'Nothing to report',
        evidence: def.ev,
      };
      groups.set(def.group, [...(groups.get(def.group) ?? []), check]);
    }

    this.checkGroups = [];
    for (const [name, checks] of groups.entries()) {
      this.checkGroups.push({ name, checks });
    }

    const summaryParts: string[] = [];
    if (tally.fail) {
      summaryParts.push(`${tally.fail} failing`);
    }
    if (tally.warn) {
      summaryParts.push(`${tally.warn} to check`);
    }
    if (tally.note + tally.ack) {
      summaryParts.push(`${tally.note + tally.ack} with notes`);
    }
    summaryParts.push(`${tally.pass} passing`);
    this.checksSummary = summaryParts.join(', ');
  }

  // ------------------------------------------------------------- report
  /** Prints a one-page report of this device (the browser's print dialog
   *  also offers "Save as PDF"). */
  printReport(): void {
    if (!this.lastDoc) return;
    this.reportMode = true;
    this.reportAt = fmtStamp(Date.now());
    document.body.classList.add(PRINT_CLASS);
    this.cdr.markForCheck();
    // give Angular a moment to draw the report before the dialog opens
    setTimeout(() => window.print(), 80);
  }

  private endReport(): void {
    document.body.classList.remove(PRINT_CLASS);
    if (!this.reportMode) return;
    this.zone.run(() => {
      this.reportMode = false;
      this.cdr.markForCheck();
      // the charts are visible again: give them their size back (the
      // window may have changed size in the print preview), then redraw
      this.resizeAfterCharts = true;
      this.scheduleCharts();
    });
  }

  /** Leaves the report view (its Close button, for browsers that don't
   *  send afterprint). */
  closeReport(): void {
    this.endReport();
  }

  // ------------------------------------------------------------- share
  openShare(): void {
    this.shareOpen = true;
    this.buildShare();
    this.cdr.markForCheck();
  }

  closeShare(): void {
    this.shareOpen = false;
    this.shareText = '';
    this.shareStatus = '';
  }

  async copyShare(): Promise<void> {
    if (this.shareLeaks.length || !this.shareText) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(this.shareText);
      copied = true;
    } catch {
      // no clipboard API (plain http): I fall back to the textarea
      const textarea = this.kit.el(this.tabIds + '-share') as HTMLTextAreaElement | null;
      if (textarea && typeof document.execCommand === 'function') {
        textarea.select();
        copied = document.execCommand('copy');
      }
    }
    this.shareStatus = copied ? 'Copied.' : 'Copy failed: select the text and copy it by hand.';
    this.cdr.markForCheck();
  }

  downloadShare(): void {
    if (this.shareLeaks.length || !this.shareText) return;
    const blob = new Blob([this.shareText], { type: 'text/markdown' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const stamp = fmtStamp(Date.now()).replace(/[: ]/g, '-');
    link.download = `fortigate-summary-${stamp}.md`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    this.shareStatus = 'Downloaded.';
  }

  /** Every identifying value of the document, for the Anonymizer. */
  private shareSecrets(doc: DashboardDocument): Secret[] {
    const info = this.deviceInfo;
    const secrets: Secret[] = [];
    const add = (kind: string, value: string | null | undefined) => {
      secrets.push({ kind, value });
    };

    add('DEVICE', doc.meta.serial);
    for (const serial of doc.meta.serials ?? []) {
      add('DEVICE', serial);
    }
    add('HOST', info?.host);
    add('HOST', info?.label);
    add('HOST', info?.hostname);
    // every group and every level of it ("France/Paris", "France" and "Paris"):
    // a level alone could also give the site away
    const deviceGroups = info ? groupsWithParents(groupsOfDevice(info)) : [];
    for (const group of deviceGroups) {
      add('SITE', group);
      add('SITE', groupLeafName(group));
    }
    for (const member of this.haMembers) {
      add('HOST', member.hostname);
      add('DEVICE', member.serial);
    }
    for (const admin of this.adminRows) {
      add('USER', admin.name);
      add('IP', admin.ip);
    }
    for (const policy of doc.policy_totals || []) {
      // "12: Allow web" -> "Allow web"
      add('POLICY', policy.name.replace(/^\d+:\s*/, ''));
    }
    for (const tunnel of this.vpnTunnels) {
      add('TUNNEL', tunnel.name);
      add('IP', tunnel.rgwy);
    }
    for (const row of doc.iface_latest || []) {
      add('ALIAS', row.alias);
      add('IP', row.ip);
    }
    for (const cert of this.certRows) {
      add('CERT', cert.name);
      add('HOST', cert.subject);
    }
    for (const server of this.ntp?.servers ?? []) {
      add('HOST', server.server);
      add('IP', server.ip);
    }
    for (const peer of this.bgpRows) {
      add('IP', peer.local_ip);
    }
    for (const peer of this.ospfRows) {
      add('IP', peer.router_id);
    }
    for (const issue of [...this.issues, ...this.issuesAcked]) {
      add('USER', issue.ack_by);
    }
    return secrets;
  }

  /** A Markdown summary of the device with every identifying value
   *  replaced, checked for leftovers before it can be copied. */
  private buildShare(): void {
    const doc = this.lastDoc;
    if (!doc) return;
    const anonymizer = new Anonymizer(this.shareSecrets(doc));
    const text = anonymizer.scrub(this.summaryMarkdown(doc));
    this.shareText = text;
    this.shareReplaced = anonymizer.replaced;
    this.shareLeaks = anonymizer.leaks(text);
    this.shareStatus = '';
  }

  private summaryMarkdown(doc: DashboardDocument): string {
    const lines: string[] = [];
    const currentRange = this.ranges.find((range) => range.id === this.histRange);
    const rangeLabel = currentRange?.label ?? this.histRange ?? '';

    lines.push(`# FortiGate summary: ${doc.meta.serial}`, '');
    lines.push(`FortiOS ${this.version}. History window ${rangeLabel}. `
      + `Generated ${fmtStamp(Date.now())}.`, '');

    lines.push('## Key figures', '');
    for (const kpi of this.kpis) {
      const extra = kpi.badge?.text ?? kpi.sub ?? '';
      const extraText = extra ? ` (${extra})` : '';
      lines.push(`- ${kpi.label}: ${kpi.value}${kpi.suffix ?? ''}${extraText}`);
    }

    lines.push('', '## Health checks', '');
    for (const group of this.checkGroups) {
      for (const check of group.checks) {
        lines.push(`- [${check.status.toUpperCase()}] ${group.name}, ${check.label}: ${check.metric}`);
      }
    }

    const issueLine = (issue: Issue): string => {
      let line = `- [${issueBadge(issue.sev).text}] ${issue.title}`;
      if (issue.detail) {
        line += `. ${issue.detail}`;
      }
      return line;
    };
    lines.push('', '## Open issues', '');
    if (this.issues.length) {
      for (const issue of this.issues) {
        lines.push(issueLine(issue));
      }
    } else {
      lines.push('- none');
    }
    if (this.issuesAcked.length) {
      lines.push('', '## Acknowledged issues', '');
      for (const issue of this.issuesAcked) {
        lines.push(issueLine(issue) + ` (${ackNote(issue, false)})`);
      }
    }

    if (this.licenseActive.length) {
      lines.push('', '## Licenses', '');
      for (const license of this.licenseActive) {
        lines.push(`- ${license.name}: ${this.licenseBadge(license).text}`);
      }
    }

    if (this.reportIfaceRows.length) {
      lines.push('', '## ' + this.reportIfaceTitle, '', '| Interface | Link | RX | TX | Util | Errors |',
                 '| --- | --- | --- | --- | --- | --- |');
      for (const row of this.reportIfaceRows) {
        const name = row.name + (row.alias ? ' ' + row.alias : '');
        const util = row.util == null ? '-' : row.util + ' %';
        lines.push(`| ${name} | ${row.link ? 'up' : 'down'} | `
          + `${fmtBps(row.rx_bps)} | ${fmtBps(row.tx_bps)} | `
          + `${util} | ${fmtNum(row.rx_errors + row.tx_errors)} |`);
      }
    }

    if (this.vpnTunnels.length) {
      lines.push('', '## IPsec tunnels', '');
      for (const tunnel of this.vpnTunnels) {
        lines.push(`- ${tunnel.name} (${tunnel.rgwy || 'no gateway'}): `
          + `${tunnel.up ? 'up' : 'down'}, ${fmtBps(tunnel.bps_now)} now`);
      }
    }

    if (this.sdwanRows.length) {
      lines.push('', '## SD-WAN members', '');
      for (const member of this.sdwanRows) {
        const loss = member.loss == null ? '-' : member.loss.toFixed(1) + ' %';
        lines.push(`- ${member.name}: ${member.status}, latency `
          + `${this.fmtMs(member.latency)}, jitter ${this.fmtMs(member.jitter)}, loss `
          + `${loss}`);
      }
    }

    const state = this.systemState;
    if (state?.available || this.ntp?.available) {
      lines.push('', '## Time and memory', '');
      if (state?.clock_skew != null) {
        lines.push(`- Clock: ${this.skewText(state.clock_skew)} of the collector`);
      }
      if (this.ntp?.available) {
        lines.push(`- NTP: ${this.ntp.reachable} of ${this.ntp.servers.length} server(s) reachable`);
      }
      if (state?.conserve != null) {
        let conserveText = 'no';
        if (state.conserve) {
          conserveText = 'yes';
        } else if (state.conserve_seen) {
          conserveText = 'no, but earlier in the window';
        }
        lines.push(`- Conserve mode: ${conserveText}`);
      }
      if (state?.last_reboot) {
        lines.push(`- Last reboot: ${fmtStamp(state.last_reboot)}`);
      }
    }

    const certsToRenew = this.certRows.filter((cert) => !cert.built_in && cert.days_left != null
      && cert.days_left < this.thresholds.cert_days.warning);
    if (certsToRenew.length) {
      lines.push('', '## Certificates to renew', '');
      for (const cert of certsToRenew) {
        lines.push(`- ${cert.name}: ${this.certBadge(cert).text}`);
      }
    }

    if (this.hasRouting) {
      lines.push('', '## Routing neighbors', '');
      for (const peer of this.bgpRows) {
        const remoteAs = peer.remote_as != null ? ' AS ' + peer.remote_as : '';
        lines.push(`- BGP ${peer.neighbor}${remoteAs}: ${peer.state}`);
      }
      for (const peer of this.ospfRows) {
        lines.push(`- OSPF ${peer.neighbor}: ${peer.state}`);
      }
    }

    if (this.events.length) {
      lines.push('', `## Recent events (${Math.min(20, this.events.length)} of ${this.eventsTotal})`, '');
      for (const event of this.events.slice(0, 20)) {
        lines.push(`- ${fmtStamp(event.t)} [${event.sev}] ${event.title}`);
      }
    }

    if (this.policyUsedRows.length) {
      lines.push('', '## Busiest policies', '');
      for (const policy of this.policyUsedRows.slice(0, 10)) {
        lines.push(`- ${policy.name}: ${fmtBps(policy.bps_now)} now, ${fmtNum(policy.hits)} hits`);
      }
    }
    lines.push('');
    return lines.join('\n');
  }

  // ------------------------------------------------------------- charts
  /** Draws the charts once the template has created their containers (a
   *  new tab, a card that just appeared). Grouped, outside Angular. */
  private scheduleCharts(): void {
    if (this.chartsTimer) {
      clearTimeout(this.chartsTimer);
    }
    this.zone.runOutsideAngular(() => {
      this.chartsTimer = setTimeout(() => {
        this.chartsTimer = null;
        // hidden behind the report: drawn when it closes (endReport)
        if (!this.destroyed && !this.fleetMode && !this.reportMode && this.lastDoc) {
          this.renderCharts(this.lastDoc);
          if (this.resizeAfterCharts) {
            this.resizeAfterCharts = false;
            this.kit.resize();
          }
        }
      });
    });
  }

  private renderCharts(doc: DashboardDocument): void {
    const kit = this.kit;
    const eventTimes = (doc.events ?? []).map((event) => event.t);
    eventTimes.sort((first, second) => first - second);
    kit.beginPass([
      doc.iface_throughput.t, doc.sessions_ts.t, doc.policy_throughput.t,
      doc.policy_sessions.t, doc.cpu_ts?.t, doc.memory_ts.t, doc.iface_errors?.t,
      doc.sdwan?.t, doc.ssl_users_ts?.t,
      eventTimes,
    ]);
    const zoom = kit.timeZoom();

    // Interface throughput (RX solid, TX dashed, one colour per interface)
    const ifaceThroughput = doc.iface_throughput;
    if (ifaceThroughput.t.length && ifaceThroughput.series.length) {
      const series: SeriesOption[] = [];
      ifaceThroughput.series.forEach((ifaceSeries, index) => {
        const color = kit.pal[index % kit.pal.length];
        // with a band, the RX area fill would just make it muddy
        const rxBand = bandOf(ifaceSeries, 'rx');
        series.push(...kit.lineWithBand(ifaceSeries.name + ' RX', ifaceThroughput.t,
          ifaceSeries.rx, rxBand, {
            color, areaStyle: rxBand.max ? undefined : { opacity: 0.06 },
          }));
        series.push(...kit.lineWithBand(ifaceSeries.name + ' TX', ifaceThroughput.t,
          ifaceSeries.tx, bandOf(ifaceSeries, 'tx'), { color, lineStyle: { type: 'dashed' } }));
      });
      kit.setChart('ch-iface', kit.base({
        xAxis: kit.timeAxis(), yAxis: kit.valAxis(fmtBps),
        tooltip: kit.bandTooltip(fmtBps), series, ...zoom,
      }), { linked: true });
    } else {
      kit.markEmpty('ch-iface',
        'Needs at least two interface polls to compute byte-counter deltas.');
    }

    // Sessions and setup rate
    const sessionsTs = doc.sessions_ts;
    if (sessionsTs.t.length) {
      kit.setChart('ch-sessions', kit.base({
        // the setup rate axis is on the right: room for the zoom tool above it
        grid: kit.gridUnderZoomTool(),
        xAxis: kit.timeAxis(),
        yAxis: [
          kit.valAxis(),
          // no axis name: it would sit under the zoom tool, the unit is in
          // the series name instead
          Object.assign(kit.valAxis(), { position: 'right', splitLine: { show: false } }),
        ],
        tooltip: kit.bandTooltip(fmtNum),
        series: [
          ...kit.lineWithBand('Active sessions', sessionsTs.t, sessionsTs.session_count,
            bandOf(sessionsTs, 'session_count'), {
              areaStyle: sessionsTs.bucketed ? undefined : { opacity: 0.1 },
              color: kit.pal[0],
            }),
          ...kit.lineWithBand('Setup rate (new/s)', sessionsTs.t, sessionsTs.setup_rate,
            bandOf(sessionsTs, 'setup_rate'), { yAxisIndex: 1, color: kit.pal[4] }),
        ],
        ...zoom,
      }), { linked: true });
    } else {
      kit.markEmpty('ch-sessions', 'No session-list history yet.');
    }

    // Throughput and sessions per policy
    const policyThroughput = doc.policy_throughput;
    if (policyThroughput.t.length && policyThroughput.series.length) {
      kit.setChart('ch-throughput', kit.base({
        xAxis: kit.timeAxis(), yAxis: kit.valAxis(fmtBps),
        tooltip: kit.bandTooltip(fmtBps),
        series: this.coloredLines(policyThroughput),
        ...zoom,
      }), { linked: true });
    } else {
      kit.markEmpty('ch-throughput',
        'Needs at least two policy-list polls to compute byte-counter deltas.');
    }
    const policySessions = doc.policy_sessions;
    if (policySessions.t.length && policySessions.series.length) {
      kit.setChart('ch-polsess', kit.base({
        xAxis: kit.timeAxis(), yAxis: kit.valAxis(),
        tooltip: kit.bandTooltip(fmtNum),
        series: this.coloredLines(policySessions),
        ...zoom,
      }), { linked: true });
    } else {
      kit.markEmpty('ch-polsess', 'No policy-list history yet.');
    }

    this.renderDaily(doc.daily_traffic);
    this.renderEvents();
    this.renderBreakdown(doc.session_traffic);
    this.renderCpuMem(doc.cpu_ts || { t: [], series: [] }, doc.memory_ts);
    if (doc.iface_errors) {
      this.renderIfaceErrors(doc.iface_errors);
    }
    this.renderSdwan(doc.sdwan);
    this.renderSsl(doc.ssl_users_ts);
    kit.endPass();
  }

  /** One line (with its band) per series, each in the next palette colour. */
  private coloredLines(timeSeries: TimeSeries,
                       // eslint-disable-next-line @typescript-eslint/no-explicit-any
                       extra: (series: NamedSeries) => { [key: string]: any } = () => ({})): SeriesOption[] {
    const kit = this.kit;
    const result: SeriesOption[] = [];
    timeSeries.series.forEach((series, index) => {
      const options = { color: kit.pal[index % kit.pal.length], ...extra(series) };
      result.push(...kit.lineWithBand(series.name, timeSeries.t, series.values, series, options));
    });
    return result;
  }

  /** The timeline as dots on the shared time axis, one row per kind:
   *  hovering one shows the same moment on every other time chart. */
  private renderEvents(): void {
    const kit = this.kit;
    if (!kit.el('ch-events')) return;
    let list = this.events;
    if (this.eventKind !== 'all') {
      list = this.events.filter((event) => event.kind === this.eventKind);
    }
    if (!list.length) {
      kit.markEmpty('ch-events', 'No state change in this window.');
      return;
    }

    const kinds = eventKindsOf(list);
    const colorOf: Record<EventSev, string> = {
      danger: kit.cssVar('--clr-danger-bd') || '#e12200',
      warning: kit.cssVar('--clr-warning-bd') || '#efc006',
      info: kit.pal[6],
      ok: kit.cssVar('--clr-success-bd') || '#62a420',
    };

    const points = [];
    for (const event of list) {
      points.push({
        value: [event.t, EVENT_KIND_LABEL[event.kind]],
        itemStyle: { color: colorOf[event.sev], borderColor: kit.cssVar('--clr-card'), borderWidth: 1 },
        // kept on the point, for the tooltip
        e: event,
      });
    }

    const tooltipText = (param: { data?: { e?: FortiEvent } }): string => {
      const event = param.data?.e;
      if (!event) return '';
      return `${esc(eventWhen(event))}<br><b>${esc(event.title)}</b>`;
    };

    kit.setChart('ch-events', kit.base({
      grid: { left: 12, right: 20, top: 30, bottom: 30, containLabel: true },
      legend: { show: false },
      xAxis: kit.timeAxis(),
      yAxis: {
        type: 'category', data: kinds.map((kind) => EVENT_KIND_LABEL[kind]),
        // every row gets its label (the container height follows the row count)
        axisLabel: { color: kit.cssVar('--clr-ink'), fontSize: 11, interval: 0 },
        axisLine: { lineStyle: { color: kit.axisLineColor() } },
        splitLine: { show: true, lineStyle: { color: kit.neutral(10, '#ececec') } },
      },
      tooltip: {
        trigger: 'item',
        formatter: tooltipText as never,
      },
      series: [{
        type: 'scatter', name: 'Events', symbolSize: 10,
        data: points,
      }] as SeriesOption[],
      ...kit.timeZoom(),
    }), { linked: true });
    // the container height follows the number of rows of the kind filter
    kit.resizeChart('ch-events');
  }

  private renderIfaceErrors(timeSeries: TimeSeries): void {
    const kit = this.kit;
    if (!kit.el('ch-iface-err')) return;
    if (!timeSeries.t.length || !timeSeries.series.length) {
      kit.markEmpty('ch-iface-err', 'No interface errors in this window.');
      return;
    }
    kit.setChart('ch-iface-err', kit.base({
      xAxis: kit.timeAxis(), yAxis: kit.valAxis(fmtRate),
      tooltip: kit.bandTooltip(fmtRate),
      // a light fill, unless there's a band
      series: this.coloredLines(timeSeries, (series) => ({
        areaStyle: series.max ? undefined : { opacity: 0.08 },
      })),
      ...kit.timeZoom(),
    }), { linked: true });
  }

  private renderSdwan(sdwan: Sdwan | undefined): void {
    const kit = this.kit;
    if (!kit.el('ch-sdwan')) return;
    if (!sdwan || !sdwan.t.length || !sdwan.series.length) {
      kit.markEmpty('ch-sdwan',
        'No SD-WAN health-check data; needs virtual-wan/health-check.');
      return;
    }

    // Three panels stacked on one time axis: latency, jitter, loss.
    const oneDecimal = (value: number | null | undefined) =>
      value == null ? '-' : value.toFixed(1);
    const thresholds = this.thresholds;
    const panels: { key: 'latency' | 'jitter' | 'loss'; label: string; level: Level }[] = [
      { key: 'latency', label: 'Latency (ms)', level: thresholds.sdwan_latency },
      { key: 'jitter', label: 'Jitter (ms)', level: thresholds.sdwan_jitter },
      { key: 'loss', label: 'Loss (%)', level: thresholds.sdwan_loss },
    ];
    const grids: object[] = [];
    const xAxes: object[] = [];
    const yAxes: object[] = [];
    const series: SeriesOption[] = [];
    // panel height, space between panels, and top margin, in px
    const PANEL_HEIGHT = 100;
    const PANEL_GAP = 34;
    const TOP = 26;

    panels.forEach((panel, panelIndex) => {
      const isLastPanel = panelIndex === panels.length - 1;
      grids.push({ left: 55, right: 20, top: TOP + panelIndex * (PANEL_HEIGHT + PANEL_GAP),
                   height: PANEL_HEIGHT });
      // only the bottom panel shows the time labels
      xAxes.push(Object.assign(kit.timeAxis(), {
        gridIndex: panelIndex,
        axisLabel: { show: isLastPanel, color: kit.cssVar('--clr-ink-dim'),
                     hideOverlap: true },
      }));
      yAxes.push(Object.assign(kit.valAxis(oneDecimal), {
        gridIndex: panelIndex, name: panel.label, nameGap: 8,
        nameTextStyle: { fontSize: 10, color: kit.cssVar('--clr-ink-dim') } }));

      sdwan.series.forEach((memberSeries, memberIndex) => {
        const band = bandOf(memberSeries, panel.key);
        series.push(...kit.lineWithBand(memberSeries.name, sdwan.t, memberSeries[panel.key], band, {
          xAxisIndex: panelIndex, yAxisIndex: panelIndex,
          color: kit.pal[memberIndex % kit.pal.length],
          areaStyle: panel.key === 'loss' && !band.max ? { opacity: 0.1 } : undefined,
          // the levels the member badges and the fleet grid are judged on;
          // ECharts only draws them once the data comes close
          markLine: memberIndex === 0 ? kit.thresholdMarks(panel.level, oneDecimal) : undefined,
        }));
      });
    });

    kit.setChart('ch-sdwan', kit.base({
      grid: grids as never, xAxis: xAxes as never, yAxis: yAxes as never,
      tooltip: kit.bandTooltip(oneDecimal),
      axisPointer: { link: [{ xAxisIndex: 'all' }] } as never,
      series,
      ...kit.timeZoom('all'),
    }), { linked: true });
  }

  private renderSsl(ssl: SslUsersTs | undefined): void {
    const kit = this.kit;
    if (!kit.el('ch-ssl')) return;
    if (!ssl || !this.hasSslUsers) {
      kit.markEmpty('ch-ssl', 'No SSL-VPN stats in this window.');
      return;
    }
    kit.setChart('ch-ssl', kit.base({
      xAxis: kit.timeAxis(), yAxis: kit.valAxis(fmtNum),
      tooltip: kit.bandTooltip(fmtNum),
      series: [
        ...kit.lineWithBand('Users', ssl.t, ssl.users, bandOf(ssl, 'users'), {
          areaStyle: ssl.bucketed ? undefined : { opacity: 0.1 },
          color: kit.pal[0] }),
        ...kit.lineWithBand('Tunnels', ssl.t, ssl.tunnels,
          bandOf(ssl, 'tunnels'), { color: kit.pal[4] }),
      ],
      ...kit.timeZoom(),
    }), { linked: true });
  }

  private renderCpuMem(cpu: TimeSeries, memory: TimeSeries): void {
    const kit = this.kit;
    const hasCpu = cpu.t.length > 0 && cpu.series.length > 0;
    const hasMemory = memory.t.length > 0 && memory.series.length > 0;
    if (!hasCpu && !hasMemory) {
      kit.markEmpty('ch-mem', 'No vdom-resource history yet.');
      return;
    }

    // One chart, CPU on the left axis, memory on the right. Warm colours for
    // CPU, cool ones for memory.
    const CPU_COLORS = [kit.pal[4], kit.pal[1], kit.pal[7], kit.pal[6]];
    const MEMORY_COLORS = [kit.pal[5], kit.pal[0], kit.pal[2], kit.pal[3]];
    const yAxes: object[] = [];
    const series: SeriesOption[] = [];
    const formatterBySeries: Record<string, Formatter> = {};

    // A side is only shown in % when every value on it, band edges
    // included, is in 0..100 (the shape changes with the FortiOS version).
    const inPctRange = (values?: Values): boolean => {
      if (!values) return true;
      return values.every((value) => value == null || (value >= 0 && value <= 100));
    };
    const isPctSide = (timeSeries: TimeSeries): boolean =>
      timeSeries.series.every((item) =>
        inPctRange(item.values) && inPctRange(item.min) && inPctRange(item.max));

    const addSide = (timeSeries: TimeSeries, label: string, colors: string[], level: Level) => {
      const axisIndex = yAxes.length;
      const isPercent = isPctSide(timeSeries);
      const formatter: Formatter = isPercent ? fmtPct : fmtNum;
      const side = axisIndex === 0 ? 'left' : 'right';

      const axis = kit.valAxis(formatter);
      // the name goes along the axis, not above it: the top right corner
      // holds the zoom tool
      Object.assign(axis, kit.sideAxisName(isPercent ? `${label} (%)` : label, side));
      if (isPercent) {
        Object.assign(axis, { min: 0, max: 100 });
      }
      axis.position = side;
      if (axisIndex === 0) {
        axis.splitLine = { lineStyle: { color: kit.neutral(10, '#ececec') } };
      } else {
        axis.splitLine = { show: false };
      }
      yAxes.push(axis);

      timeSeries.series.forEach((item, index) => {
        let name = `${label} ${item.name}`;
        if (item.name.toLowerCase() === label.toLowerCase()) {
          name = label;
        }
        formatterBySeries[name] = formatter;

        // Where the CPU / memory issues start (percent axes only). Each set
        // names itself and labels its own end of the chart, so the CPU and
        // memory levels stay readable when they're close.
        let markLine = undefined;
        if (index === 0 && isPercent) {
          markLine = kit.thresholdMarks(level, fmtPct, {
            name: label, at: side === 'left' ? 'start' : 'end',
            dotted: side === 'right', below: side === 'right',
          });
        }
        series.push(...kit.lineWithBand(name, timeSeries.t, item.values, item, {
          yAxisIndex: axisIndex,
          color: colors[index % colors.length],
          areaStyle: index === 0 && !item.max ? { opacity: 0.07 } : undefined,
          markLine,
        }));
      });
    };
    if (hasCpu) {
      addSide(cpu, 'CPU', CPU_COLORS, this.thresholds.cpu);
    }
    if (hasMemory) {
      addSide(memory, 'Memory', MEMORY_COLORS, this.thresholds.memory);
    }

    kit.setChart('ch-mem', kit.base({
      // room on each side for the tick labels and the vertical axis name,
      // and at the top for the zoom tool, which would otherwise sit on the
      // "100 %" label of the memory axis (see gridUnderZoomTool)
      grid: kit.gridUnderZoomTool({ left: 64, right: 64, bottom: 55 }),
      xAxis: kit.timeAxis(),
      yAxis: yAxes as never,
      tooltip: kit.bandTooltip(fmtNum, formatterBySeries),
      series,
      ...kit.timeZoom(),
    }), { linked: true });
  }

  /** Horizontal bar chart, biggest on top. */
  private hbar(id: string, items: NameValue[], valueFormatter: Formatter,
               name: string, color: string): void {
    const kit = this.kit;
    if (!kit.el(id)) return;
    if (!items.length) {
      kit.markEmpty(id, 'No session details in this range.');
      return;
    }
    // ECharts draws a category axis bottom-up, so I reverse the lists to
    // get the biggest on top
    const names = items.map((item) => item.name).reverse();
    const values = items.map((item) => item.value).reverse();
    kit.setChart(id, kit.base({
      grid: { left: 12, right: 25, top: 10, bottom: 40, containLabel: true },
      tooltip: { axisPointer: { type: 'shadow' }, valueFormatter: valueFormatter as never },
      xAxis: kit.valAxis(valueFormatter),
      yAxis: {
        type: 'category', data: names,
        axisLabel: { color: kit.cssVar('--clr-ink'), fontSize: 11,
                     width: 125, overflow: 'truncate' },
        axisLine: { lineStyle: { color: kit.axisLineColor() } },
      },
      series: [{ name, type: 'bar', color, data: values }],
    }));
  }

  private pie(id: string, items: NameValue[], valueFormatter: Formatter): void {
    const kit = this.kit;
    if (!kit.el(id)) return;
    if (!items.length) {
      kit.markEmpty(id, 'No session details in this range.');
      return;
    }
    const tooltipText = (param: { name: string; value: number; percent: number }) =>
      `${esc(param.name)}<br>${valueFormatter(param.value)} (${param.percent}%)`;
    kit.setChart(id, kit.base({
      tooltip: {
        trigger: 'item',
        formatter: tooltipText as never,
      },
      series: [{
        type: 'pie', radius: ['45%', '70%'], center: ['50%', '44%'],
        itemStyle: { borderColor: kit.axisLineColor(), borderWidth: 1 },
        // slices under 5 degrees keep their legend entry but get no label
        minShowLabelAngle: 5,
        label: { color: kit.cssVar('--clr-ink'), fontSize: 11,
                 formatter: '{b}\n{d}%', alignTo: 'labelLine' },
        labelLine: { length: 12, length2: 10, maxSurfaceAngle: 80 },
        labelLayout: { hideOverlap: true },
        data: items,
      }],
    }), { describeValues: valueFormatter });
  }

  private renderDaily(daily: DailyTraffic): void {
    const kit = this.kit;
    if (!kit.el('ch-daily')) return;
    if (!daily.days.length) {
      kit.markEmpty('ch-daily', 'Needs at least two interface polls.');
      return;
    }
    kit.setChart('ch-daily', kit.base({
      grid: { left: 12, right: 20, top: 25, bottom: 50, containLabel: true },
      xAxis: {
        type: 'category', data: daily.days,
        axisLine: { lineStyle: { color: kit.axisLineColor() } },
        axisLabel: { color: kit.cssVar('--clr-ink-dim') },
      },
      yAxis: kit.valAxis(fmtBytes),
      tooltip: { axisPointer: { type: 'shadow' }, valueFormatter: fmtBytes as never },
      series: [
        { name: 'Received', type: 'bar', stack: 'd', color: kit.pal[0],
          data: daily.rx, barMaxWidth: 46 },
        { name: 'Sent', type: 'bar', stack: 'd', color: kit.pal[4],
          data: daily.tx, barMaxWidth: 46 },
      ],
    }));
  }

  private renderBreakdown(sessionTraffic: SessionTraffic): void {
    const kit = this.kit;
    const bucket = sessionTraffic && sessionTraffic.bucket;
    const EMPTY_MESSAGE = 'No session details in this window.';
    if (!bucket) {
      const chartIds = ['ch-senders', 'ch-proto', 'ch-dports', 'ch-apps',
                        'ch-scope', 'ch-services', 'ch-receivers'];
      for (const id of chartIds) {
        kit.markEmpty(id, 'No session details collected yet.');
      }
      return;
    }

    // Top senders. Older backends only send the "talkers" field.
    let senders = bucket.senders;
    if (!senders) {
      senders = (bucket.talkers || []).map((talker) =>
        ({ name: talker.name, value: talker.sent + talker.rcvd }));
    }
    this.pie('ch-senders', senders, fmtBytes);

    if (bucket.protocols.length) {
      this.pie('ch-proto', bucket.protocols, fmtNum);
    } else {
      kit.markEmpty('ch-proto', EMPTY_MESSAGE);
    }

    // Top destination ports, by sessions or by volume (header toggle)
    if (this.dportsByVolume && !bucket.dports_bytes) {
      kit.markEmpty('ch-dports', 'Per-port volume needs the updated collector.');
    } else if (this.dportsByVolume) {
      this.hbar('ch-dports', bucket.dports_bytes!, fmtBytes, 'Bytes', kit.pal[9]);
    } else {
      this.hbar('ch-dports', bucket.dports, fmtNum, 'Sessions', kit.pal[9]);
    }

    // Top applications: only when the card is uncommented in the template.
    // FortiOS only reports application data when the policies have an
    // Application Control profile.
    if (kit.el('ch-apps')) {
      const noApps = !bucket.apps || !bucket.apps.length;
      if (noApps && bucket.distinct_sessions > 0) {
        kit.markEmpty('ch-apps',
          'Sessions carry no application info; apply an Application Control '
          + 'profile to the firewall policies to populate this chart.');
      } else {
        this.hbar('ch-apps', bucket.apps, fmtBytes, 'Bytes', kit.pal[5]);
      }
    }

    // Internal vs external: a sankey (source scope, service, destination
    // scope), or the old pie for documents from an older backend.
    const flows = bucket.scope_flows;
    if (!kit.el('ch-scope')) {
      // the card is hidden, nothing to draw
    } else if (flows && flows.links.length) {
      this.renderScopeSankey(flows);
    } else if (bucket.scope && bucket.scope.length) {
      this.pie('ch-scope', bucket.scope, fmtBytes);
    } else {
      kit.markEmpty('ch-scope', EMPTY_MESSAGE);
    }

    this.pie('ch-services', bucket.services || [], fmtBytes);
    this.pie('ch-receivers', bucket.receivers || bucket.emitters || [], fmtBytes);
  }

  private renderScopeSankey(flows: ScopeFlows): void {
    const kit = this.kit;
    type SankeyParam = {
      dataType?: string; name?: string; value?: number;
      data?: { source?: string; target?: string };
    };
    const tooltipText = (param: SankeyParam): string => {
      // an edge is a link between two nodes, otherwise it's a node
      if (param.dataType === 'edge') {
        return `${esc(param.data?.source || '')} → ${esc(param.data?.target || '')}`
          + `<br><b>${fmtBytes(param.value || 0)}</b>`;
      }
      return `<b>${esc(param.name || '')}</b><br>${fmtBytes(param.value || 0)}`;
    };
    kit.setChart('ch-scope', kit.base({
      tooltip: {
        trigger: 'item',
        formatter: tooltipText as never,
      },
      series: [{
        type: 'sankey',
        left: 8, top: 12, right: 150, bottom: 12,
        nodeWidth: 12, nodeGap: 10, nodeAlign: 'justify',
        draggable: false,
        emphasis: { focus: 'adjacency' },
        data: flows.nodes,
        links: flows.links,
        lineStyle: { color: 'gradient', curveness: 0.5, opacity: 0.35 },
        itemStyle: { borderWidth: 0 },
        label: { fontFamily: FONT, fontSize: 11,
                 color: kit.cssVar('--clr-ink-strong') },
      } as never],
    }), { describeValues: fmtBytes });
  }

}

/** The band stored as <key>_min / <key>_max on a series with several fields. */
export function bandOf(source: object, key: string): Band {
  const fields = source as Record<string, Values | undefined>;
  return { min: fields[key + '_min'], max: fields[key + '_max'] };
}

/** The 'current' vdom-resource series, otherwise the first one (same rule
 *  as dashboard.py). */
function pickSeries(timeSeries: TimeSeries | undefined): NamedSeries | null {
  const list = timeSeries?.series ?? [];
  const current = list.find((series) => /current/.test(series.name));
  return current || list[0] || null;
}

/** The value `stepsBack` points from the end (1 = the last one), or null
 *  when the list is too short. */
function valueFromEnd(values: Values, stepsBack: number): number | null {
  const index = values.length - stepsBack;
  if (index < 0) return null;
  return values[index];
}

/** The last value that is not null, or null. */
function lastValue(values?: Values): number | null {
  const list = values ?? [];
  for (let index = list.length - 1; index >= 0; index--) {
    if (list[index] != null) {
      return list[index];
    }
  }
  return null;
}

export type { Sev };
